import { createContext, useContext, useState, ReactNode } from 'react';

export type Language = 'en' | 'el' | 'it' | 'de' | 'fr' | 'nl';

const translations: Record<Language, Record<string, string>> = {
  en: {
    // Nav & common
    home: 'Home', about: 'About', apartments: 'Apartments', destinations: 'Destinations',
    reviews: 'Reviews', booking: 'Booking', contact: 'Contact',
    // Hero
    heroTitle: 'Sivota Royal', heroSubtitle: 'Luxury Retreat',
    heroDesc: 'Two exclusive residences designed for unforgettable Mediterranean escapes.',
    // Welcome / Intro
    welcome: 'Welcome', welcomeTitle: 'Where Luxury Meets the Ionian Sea',
    welcomeDesc: 'Nestled on the pristine coastline of Sivota, our two exclusive apartments — Hera and Zeus — offer an unparalleled experience of Mediterranean luxury. Every detail has been curated to create your perfect escape.',
    // Explore
    explore: 'Explore', exploreHera: 'Explore Hera', exploreZeus: 'Explore Zeus',
    theApartments: 'The Apartments',
    heraDesc: 'Named after the queen of the gods, Hera Apartment embodies grace and elegance. Wake to stunning sea views and bask in the warm Mediterranean light.',
    zeusDesc: 'Commanding and refined, Zeus Apartment captures the spirit of the king of Olympus. Spacious interiors, panoramic views, and uncompromising luxury.',
    // Reviews / Experience
    guestExperiences: 'Guest Experiences', whatGuestsSay: 'What Our Guests Say',
    theExperience: 'The Experience', whySivotaRoyal: 'Why Sivota Royal',
    privateLuxury: 'Private Luxury', stunningViews: 'Stunning Views',
    nearBeaches: 'Near Beaches', mediterraneanLife: 'Mediterranean Life',
    // Discover
    discover: 'Discover', sivotaBeyond: 'Sivota & Beyond',
    sivotaBeyondDesc: 'Explore hidden beaches, charming harbours, boat trips through crystal-clear waters, and authentic Greek tavernas. Sivota is a gem waiting to be discovered.',
    exploreDestinations: 'Explore Destinations',
    // Location
    location: 'Location', findUs: 'Find Us',
    planEscape: 'Plan Your Escape to Sivota',
    planEscapeDesc: 'Experience relaxed Mediterranean living at Sivota Royal.',
    checkAvailability: 'Check Availability', sendEnquiry: 'Send Enquiry',
    quickLinks: 'Quick Links', bookNow: 'Book Now',
    footerDesc: 'Luxury apartments in the heart of Sivota, Greece. Experience the beauty of the Ionian coast.',
    allRights: 'All rights reserved.',
    // Apartment pages
    overview: 'Overview', amenities: 'Amenities', gallery: 'Gallery',
    graceElegance: 'Grace & Elegance', powerRefinement: 'Power & Refinement',
    everythingYouNeed: 'Everything You Need',
    readyExperience: 'Ready to Experience',
    // About
    ourStory: 'Our Story', philosophy: 'Philosophy',
    craftedTitle: 'Crafted for Those Who Seek More',
    medLiving: 'Mediterranean Living', beautyOfSivota: 'The Beauty of Sivota',
    theResidences: 'The Residences', ourApartments: 'Our Apartments',
    // Reviews page
    testimonials: 'Testimonials', guestReviews: 'Guest Reviews',
    basedOnReviews: 'Based on guest reviews',
    // Booking
    reserve: 'Reserve', bookYourStay: 'Book Your Stay',
    option1: 'Option 1', option2: 'Option 2',
    sendAnEnquiry: 'Send an Enquiry', bookDirectly: 'Book Directly',
    fullName: 'Full Name', email: 'Email Address', phone: 'Phone Number',
    checkin: 'Check-in', checkout: 'Check-out', guests: 'Number of Guests',
    message: 'Message', sendMessage: 'Send Message',
    bookDirectlyDesc: 'For instant booking and availability, visit our Booking.com page. Check real-time availability, see current rates, and book your Mediterranean escape with confidence.',
    // Contact
    getInTouch: 'Get in Touch', contactDetails: 'Contact Details',
    sivotaGreece: 'Sivota, Thesprotia, Greece',
    destinationsIntro: 'Sivota and its surroundings offer a wealth of experiences — from pristine beaches and crystal waters to authentic Greek village life and culinary delights.',
    whatsappMessage: 'Hello! I\'m interested in booking at Sivota Royal Luxury Retreat.',

    // ═══ NEW KEYS ═══
    // Index hero
    sivotaIonianGreece: 'Sivota · Ionian Coast · Greece',
    whereSeaMeetsSky: 'Where the Sea Meets the Sky',
    heroLongDesc: 'Two luxury stone-built apartments perched above the Ionian Sea — wake up to Corfu, fall asleep to sunsets',
    viewApartments: 'View Apartments',
    // Trust bar
    ratingLabel: '9.2 Rating',
    exceptionalBooking: 'Exceptional on Booking.com',
    onBookingCom: '9.2 on Booking.com',
    hilltopViews: '🏔️ Hilltop Panoramic Views',
    twoMinBeach: '🌊 2 min to Beach',
    privatePool: '🏊 Private Pool',
    instantConfirmation: 'Instant Confirmation',
    bookWithConfidence: 'Book with confidence',
    freeCancellation: 'Free Cancellation',
    flexibleBooking: 'Flexible booking options',
    // Index intro
    whereMedComesAlive: 'Where the Mediterranean Comes Alive',
    indexIntroDesc: 'Nestled atop a picturesque hill in the charming village of Sivota, our stone-built apartments offer panoramic views of the Ionian Sea and Corfu island — a sight so mesmerizing it will leave you speechless.',
    // Apartment cards
    heraPanoramicDesc: 'A panoramic sanctuary with unlimited views of the Ionian Sea and the island of Corfu. Begin your day with breakfast in the sunshine and end your evenings savouring the most beautiful sunsets.',
    zeusPoolDesc: 'A deluxe retreat with an impressive private pool and panoramic Ionian views. The idyllic stone-built courtyard creates an unforgettable Mediterranean escape.',
    seaViewsTerrace: 'Sea views & terrace',
    privatePoolCourtyard: 'Private pool & courtyard',
    // Amenity labels
    twoBeds: '2 Beds', seaViews: 'Sea Views', wiFi: 'Wi-Fi', ac: 'A/C',
    kitchen: 'Kitchen', terrace: 'Terrace', pool: 'Pool', views: 'Views',
    twoBedrooms: '2 Bedrooms', freeWiFi: 'Free Wi-Fi', fullKitchen: 'Full Kitchen',
    privatePoolLabel: 'Private Pool', panoramicViews: 'Panoramic Views',
    seaCorfuViews: 'Sea & Corfu Views', privateTerrace: 'Private Terrace',
    airConditioning: 'Air Conditioning', highSpeedWiFi: 'High-Speed Wi-Fi',
    smartTV: 'Smart TV', washingMachine: 'Washing Machine',
    stoneVeranda: 'Stone Veranda', panoramicSeaViews: 'Panoramic Sea Views',
    // Quote
    bestViewsQuote: '"The best views in all of Sivota"',
    ourGuests: '— Our Guests',
    // Experience section
    medLivingFinest: 'Mediterranean Living at Its Finest',
    sunDrenchedTerraces: 'Sun-Drenched Terraces',
    sunDrenchedDesc: 'Breakfast in the sunshine, dinner under the stars',
    endlessBlueViews: 'Endless Blue Views',
    endlessBlueDesc: 'Panoramic Ionian Sea and Corfu island vistas',
    crystalClearBeaches: 'Crystal-Clear Beaches',
    crystalClearDesc: 'Hidden coves and turquoise waters minutes away',
    authenticGreekLife: 'Authentic Greek Life',
    authenticGreekDesc: 'Tavernas, harbours, and island-hopping adventures',
    // Gallery
    sunSeaStone: 'Sun, Sea & Stone',
    // Destinations
    islandHoppingFromSivota: 'Island Hopping from Sivota',
    islandHoppingDesc: 'Explore the jewels of the Ionian Sea — all within easy reach of your doorstep',
    readAllReviews: 'Read all reviews',
    // CTA
    startPlanning: 'Start Planning',
    yourMedEscape: 'Your Mediterranean Escape Awaits',
    bookDirectlyBestRates: 'Book directly for the best rates and a personal welcome from your host Dimitris',
    yourStoryStartsHere: 'Your Story Starts Here',
    readyExperienceSivotaRoyal: 'Ready to Experience Sivota Royal?',
    bookDirectlyBestRatesDimitris: 'Book directly for the best rates and a personal welcome from Dimitris',
    readyExperienceSivota: 'Ready to Experience Sivota?',

    // About page
    builtWithLove: 'Built with Love, Bathed in Sunlight',
    notJustPlace: 'This Isn\'t Just a Place to Stay',
    aboutIntro1: 'Imagine waking to the sound of the sea, stepping onto a sun-warmed terrace, and watching light dance across the Ionian towards Corfu. The scent of wild herbs fills the air. Your coffee tastes better here.',
    aboutIntro2: 'Sivota Royal wasn\'t designed in an office — it was built by hand, stone by stone, by a family who believes the best holidays happen in places made with love.',
    whatSetsUsApart: 'What Sets Us Apart',
    moreThanAccommodation: 'More Than Accommodation',
    handcrafted: 'Handcrafted',
    handcraftedDesc: 'Every stone placed by hand, every detail considered. A labour of love spanning generations.',
    bestViews: 'Best Views',
    bestViewsDesc: 'Hilltop terraces with uninterrupted panoramas of the Ionian Sea and Corfu island.',
    twoMinToBeach: '2 Min to Beach',
    twoMinToBeachDesc: 'Crystal-clear turquoise waters just moments away. Hidden coves to discover.',
    authentic: 'Authentic',
    authenticDesc: 'No crowds, no rush. The gentle rhythm of Greek coastal life — swims, siestas, sunsets.',
    theSetting: 'The Setting',
    everyMorningGift: 'Every Morning Feels Like a Gift',
    settingDesc1: 'Golden sunlight streams through your windows. You step onto your private terrace to the gentle sound of the sea below. The scent of olive groves and wild thyme fills the air.',
    settingDesc2: 'This is not a dream — this is your morning at Sivota Royal. A place where time slows down, and every moment becomes a memory.',
    greeceBestKeptSecret: 'Greece\'s Best-Kept Secret',
    locationDesc1: 'Sivota is a hidden gem on the Thesprotia coast — where turquoise waters meet cypress-covered hillsides. Far from the crowds, yet blessed with crystal-clear beaches and authentic tavernas.',
    locationDesc2: 'Explore hidden coves by boat, dine on freshly caught fish at the harbour, or watch the sunset paint the sky in gold and rose from your terrace.',
    quoteNotHotel: '"We didn\'t build a hotel. We built the place we\'d want to stay."',
    ownerName: '— Dimitris Vlachos, Owner',
    theProperty: 'The Property',
    everyDetailConsidered: 'Every Detail, Considered',
    personalWelcome: 'A Personal Welcome',
    meetDimitris: 'Meet Dimitris',
    dimitrisQuote1: '"I\'m Dimitris, and Sivota Royal is my life\'s work. Since I was young, I\'ve worked alongside my father, laying every stone, shaping every terrace, planting every olive tree. This place isn\'t a business — it\'s our family\'s gift to people who love the sea."',
    dimitrisQuote2: '"When you arrive, I\'ll be here to welcome you personally. I\'ll show you the best hidden beaches, recommend the taverna with the freshest fish, and make sure your stay is everything you dreamed of."',
    yourHost: 'Your Host',
    dimitrisMadeFeel: '"Dimitris made us feel like family"',

    // Apartments listing page
    twoExceptionalResidences: 'Two exceptional residences, each with its own character, offering the finest in Mediterranean luxury.',
    heraExtendedDesc: 'This stunning panoramic apartment offers unlimited views of the Ionian Sea and the enchanting island of Corfu.',
    zeusExtendedDesc: 'A deluxe apartment with an impressive private pool and panoramic Ionian views.',

    // Booking page
    stayDetails: 'Stay Details',
    yourDetails: 'Your Details',
    selectYourDates: 'Select your dates',
    total: 'Total',
    discount: 'Discount',
    couponCode: 'Coupon Code',
    enterCode: 'Enter code',
    apply: 'Apply',
    continueBtn: 'Continue',
    back: 'Back',
    sending: 'Sending...',
    yourSelection: 'Your Selection',
    editSelection: 'Edit selection',
    apartment: 'Apartment',
    chatWithUs: 'Chat With Us Directly',
    chatWithUsDesc: 'Prefer to message? Reach us instantly on WhatsApp for quick answers about availability, rates, and special requests.',
    chatOnWhatsApp: '💬 Chat on WhatsApp',
    noPricingSet: 'No pricing set for this period — we\'ll confirm the rate.',
    minimumStay: 'Minimum stay is {min} nights for this period',
    thankYouEnquiry: 'Thank you! Your enquiry has been sent. We will be in touch shortly.',
    fillNameEmail: 'Please fill in your name and email.',
    selectApartment: 'Please select an apartment.',
    selectDates: 'Please select check-in and check-out dates.',
    invalidCoupon: 'Invalid coupon code',

    // Contact
    viewOnBooking: 'View on Booking.com',
    fillRequiredFields: 'Please fill in all required fields.',
    messageSent: 'Message sent! We will get back to you soon.',

    // Reviews page
    joinOurGuests: 'Join Our Guests',
    createYourStory: 'Create Your Own Story',
    featuredQuote: '"The best views in all of Sivota — a truly unforgettable experience"',
    featuredQuoteAuthor: '— Maria K., Athens',

    // Destinations page
    hiddenCovesDesc: 'Hidden coves, island adventures, and authentic Greek experiences — all from your doorstep',
    secludedBeaches: 'Secluded Beaches',
    secludedBeachesDesc: 'Crystal-clear waters and white pebble beaches hidden along the coastline. Bella Vraka, Mega Ammos, Mikri Ammos — countless secret coves await.',
    sivotaCoastline: 'Sivota Coastline',
    boatTrips: 'Boat Trips',
    boatTripsDesc: 'Explore the Ionian islands and hidden sea caves on a private or shared boat excursion. Swim in waters so clear the boats seem to float on air.',
    ionianSea: 'Ionian Sea',
    sivotaHarbour: 'Sivota Harbour',
    sivotaHarbourDesc: 'Stroll along the charming waterfront, browse local shops, and enjoy fresh seafood at traditional Greek tavernas.',
    sivotaVillage: 'Sivota Village',
    localDining: 'Local Dining',
    localDiningDesc: 'From seaside tavernas to hilltop restaurants, savour authentic Greek cuisine made with fresh, locally sourced ingredients.',
    sivotaSurrounds: 'Sivota & Surrounds',
    islandHopping: 'Island Hopping',
    jewelsOfIonian: 'Jewels of the Ionian',
    islandHoppingIntro: 'From Sivota, explore three of the most beautiful islands in the Mediterranean',
    corfuDesc: 'UNESCO Old Town, Achilleion Palace, and stunning beaches like Paleokastritsa. Vibrant markets, historic fortresses, and world-class dining.',
    corfuTime: '1.5 hr by ferry',
    paxosDesc: 'Famous for dramatic sea caves, turquoise waters, and the charming villages of Gaios, Lakka, and Loggos. Olive groves blanket the island.',
    paxosTime: '1 hr by boat',
    antipaxosDesc: 'Home to Voutoumi and Vrika — two of the most beautiful beaches in Greece. Caribbean-like waters perfect for swimming and snorkelling.',
    antipaxosTime: '1.5 hr by boat',
    thesprotiaRegion: 'Thesprotia Region',
    natureHiking: 'Nature & Hiking',
    natureHikingDesc1: 'Explore olive groves, ancient trails, and lush green hillsides that offer panoramic views of the Ionian coast. The Thesprotia region is a paradise for nature lovers and photographers.',
    natureHikingDesc2: 'From mountain villages to coastal paths, every walk reveals a new perspective of this unspoiled corner of Greece.',
    yourGateway: 'Your Gateway',
    startIonianAdventure: 'Start Your Ionian Adventure',
    ionianAdventureDesc: 'From Sivota, explore Corfu, Paxos, and Antipaxos — some of the most beautiful islands in the Mediterranean.',

    // Hera apartment page
    heraHeroDesc: 'The best window view of Sivota — endless Ionian blue and Corfu sunsets',
    masterBedroom: 'Master Bedroom',
    heraMasterTitle: 'Dreamy Bedroom with Unrivaled Sivota Sunsets',
    heraMasterDesc: 'This stunning panoramic bedroom offers unlimited views of the Ionian Sea and the enchanting island of Corfu. Begin your day with breakfast in the sunshine, taking in the breathtaking scenery, and end your evenings savoring the most beautiful sunset Sivota has to offer.',
    secondaryBedroom: 'Secondary Bedroom',
    heraSecondaryTitle: 'Charming Bedroom with Stunning Sea Views',
    heraSecondaryDesc: 'This delightful secondary bedroom comfortably accommodates up to three guests, making it perfect for the couple\'s children or friends. Like the master bedroom, it offers magnificent views of the Ionian Sea and Corfu.',
    kitchenDining: 'Kitchen & Dining',
    heraKitchenTitle: 'Luxurious Kitchen with Traditional Greek Design',
    heraKitchenDesc: 'This luxurious kitchen features an incredible design of stone and wood, reminiscent of traditional old Greek houses. The stone bar provides a comfortable and stylish space to cook and enjoy your meals, with balcony views of the Ionian Sea and Corfu.',
    livingRoom: 'Living Room',
    heraLivingTitle: 'Regal Living Room with Traditional Elegance',
    heraLivingDesc: 'This grand living room exudes an air of royalty with its intricately carved traditional furniture and majestic design. Spacious enough to accommodate all guests comfortably, it combines classic elegance with modern conveniences.',
    secureYourDates: 'Secure your dates and experience the magic of Sivota from our hilltop sanctuary.',
    exceptionalOnBooking: 'Exceptional · Booking.com',
    // Hera overview
    heraOverviewDesc1: 'Named after the queen of the Greek gods, Hera Apartment embodies grace, warmth, and timeless beauty. Every element has been thoughtfully designed to create a sanctuary of comfort overlooking the Ionian Sea.',
    heraOverviewDesc2: 'Wake to the gentle sound of waves, enjoy your morning coffee on the private terrace as the Mediterranean sun bathes the coastline in golden light.',

    // Zeus apartment page
    zeusHeroDesc: 'Private pool, panoramic Ionian views, and traditional stone craftsmanship',
    zeusOverviewDesc1: 'Bearing the name of the king of the gods, Zeus Apartment commands attention with its spacious interiors, natural stone accents, and sweeping panoramic views.',
    zeusOverviewDesc2: 'Discover the enchanting courtyard, a perfect blend of nature and traditional craftsmanship. At its heart lies a picturesque round pool, surrounded by elegant sun loungers.',
    courtyardPool: 'Courtyard & Pool',
    zeusCourtTitle: 'Idyllic Courtyard with Stunning Pool and Panoramic Views',
    zeusCourtDesc: 'Discover the enchanting courtyard — a lush green oasis with a beautifully manicured lawn, stone accents, and a picturesque round pool. The courtyard is adorned with a pergola of cascading pink bougainvillea, set against breathtaking panoramic views of the Ionian Sea.',
    zeusMasterTitle: 'Charming Master Bedroom with Traditional Wooden Furnishings',
    zeusMasterDesc: 'Step into the enchanting master bedroom, adorned with beautiful handmade wooden furniture. Open the balcony door to reveal a perfect veranda with a traditional wooden roof, ideal for sipping your morning coffee while basking in the tranquil natural beauty.',
    twinBedroom: 'Twin Bedroom',
    zeusTwinTitle: 'Cozy Twin Bedroom with Expansive Balcony & Sea Views',
    zeusTwinDesc: 'Featuring two comfortable single beds, this room ensures a restful stay. Step outside onto the spacious balcony to soak in the stunning views of both the mountains and the sea.',
    stoneVerandaLabel: 'Stone Veranda',
    zeusVerandaTitle: 'Traditional Stone Veranda with Panoramic Ionian Views',
    zeusVerandaDesc: 'Step onto the exquisite veranda, constructed with hand-carved stone and a classic wooden roof. The spacious dining area is ideal for the whole family to gather and enjoy meals together while taking in the breathtaking panoramic views of the Ionian Sea.',
    secureYourDatesZeus: 'Secure your dates and experience the ultimate Greek retreat with pool and panoramic views.',

    // Amenity lists - Hera
    seaViewBedrooms: 'Sea-view bedrooms', premiumBedLinens: 'Premium bed linens',
    fullyEquippedKitchen: 'Fully equipped kitchen', privateTerraceAmenity: 'Private terrace',
    airConditioningAmenity: 'Air conditioning', highSpeedWiFiAmenity: 'High-speed Wi-Fi',
    smartTVAmenity: 'Smart TV', washingMachineAmenity: 'Washing machine',
    hairDryer: 'Hair dryer', ironIroningBoard: 'Iron & ironing board',
    toiletries: 'Toiletries', beachTowels: 'Beach towels',
    stoneBuiltDesign: 'Stone-built design', woodenDetails: 'Wooden details',
    traditionalArchitecture: 'Traditional architecture', modernComforts: 'Modern comforts',
    // Amenity lists - Zeus
    privatePoolAmenity: 'Private pool', panoramicSeaViewsAmenity: 'Panoramic sea views',
    spaciousCourtyard: 'Spacious courtyard',
    stoneBuiltVeranda: 'Stone-built veranda', woodenFurnishings: 'Wooden furnishings',
    bougainvilleaPergola: 'Bougainvillea pergola', sunLoungers: 'Sun loungers',

    // Cookie consent
    weValuePrivacy: 'We value your privacy',
    cookieDesc: 'We use essential cookies to ensure our website functions properly. No tracking or advertising cookies are used. By continuing to browse, you agree to our use of essential cookies.',
    decline: 'Decline',
    accept: 'Accept',

    // Admin Dashboard
    adminDashboard: 'Admin Dashboard',
    dashboardOverview: 'Dashboard Overview',
    enquiries: 'Enquiries',
    bookings: 'Bookings',
    seasonsPricing: 'Seasons & Pricing',
    coupons: 'Coupons',
    photos: 'Photos',
    revenue: 'Revenue',
    signOut: 'Sign Out',
    pendingEnquiries: 'Pending Enquiries',
    totalBookings: 'Total Bookings',
    totalRevenue: 'Total Revenue',
    depositsReceived: 'Deposits Received',
    remainingBalance: 'Remaining Balance',
    totalEnquiries: 'Total Enquiries',
    // Admin Enquiries
    all: 'All',
    pending: 'Pending',
    accepted: 'Accepted',
    declined: 'Declined',
    adminAccept: 'Accept',
    adminDecline: 'Decline',
    edit: 'Edit',
    delete: 'Delete',
    save: 'Save',
    cancel: 'Cancel',
    noEnquiriesFound: 'No enquiries found',
    submitted: 'Submitted',
    nights: 'Nights',
    coupon: 'Coupon',
    // Admin Bookings
    upcoming: 'Upcoming',
    past: 'Past',
    unpaid: 'Unpaid',
    depositReceived: 'Deposit Received',
    fullyPaid: 'Fully Paid',
    noUpcomingBookings: 'No upcoming bookings',
    noPastBookings: 'No past bookings',
    name: 'Name',
    emailLabel: 'Email',
    phoneLabel: 'Phone',
    apartmentLabel: 'Apartment',
    dates: 'Dates',
    guestsLabel: 'Guests',
    totalAmount: 'Total Amount',
    depositPaid: 'Deposit Paid',
    paymentStatus: 'Payment Status',
    notes: 'Notes',
    bookingUpdated: 'Booking updated',
    bookingDeleted: 'Booking deleted',
    deleteBookingConfirm: 'Delete booking for {name}? This cannot be undone.',
    addNotesPlaceholder: 'Add notes about this booking...',
    notesSaved: 'Notes saved',
    // Admin Seasons
    activeSeason: 'Active Season',
    pricingPeriods: 'Pricing Periods',
    activeSeasonDesc: 'Define the overall active season. Only dates within this period will be selectable by guests on the booking form.',
    pricingPeriodsDesc: 'Create pricing periods within the active season. Each period defines nightly rates and minimum stay for Hera & Zeus.',
    setActiveSeason: 'Set Active Season',
    addPeriod: 'Add Period',
    dateRange: 'Date Range',
    selectDatesAdmin: 'Select dates',
    minStay: 'Min Stay (nights)',
    heraPriceNight: 'Hera Price/Night (€)',
    zeusPriceNight: 'Zeus Price/Night (€)',
    create: 'Create',
    update: 'Update',
    inactive: 'Inactive',
    noPricingPeriods: 'No pricing periods configured yet',
    fillRequired: 'Fill in all required fields',
    updatedSuccessfully: 'Updated successfully',
    createdSuccessfully: 'Created successfully',
    deleted: 'Deleted',
    enquiryDeclinedDeleted: 'Enquiry declined & deleted',
    enquiryDeleted: 'Enquiry deleted',
    enquiryUpdated: 'Enquiry updated',
    bookingCreatedFor: 'Booking created for {name}',
    failedToCreate: 'Failed to create booking',
    failedToSave: 'Failed to save',
    failedToDelete: 'Failed to delete',
    failedToUpdate: 'Failed to update',
    declineDeleteConfirm: 'Decline and delete this enquiry?',
    deleteEnquiryConfirm: 'Delete enquiry from {name}? This cannot be undone.',
    created: 'Created',
    enquiryId: 'Enquiry ID',
  },
  el: {
    home: 'Αρχική', about: 'Σχετικά', apartments: 'Διαμερίσματα', destinations: 'Προορισμοί',
    reviews: 'Κριτικές', booking: 'Κράτηση', contact: 'Επικοινωνία',
    heroTitle: 'Sivota Royal', heroSubtitle: 'Πολυτελές Καταφύγιο',
    heroDesc: 'Δύο αποκλειστικές κατοικίες σχεδιασμένες για αξέχαστες μεσογειακές αποδράσεις.',
    welcome: 'Καλώς Ήρθατε', welcomeTitle: 'Όπου η Πολυτέλεια Συναντά το Ιόνιο Πέλαγος',
    welcomeDesc: 'Φωλιασμένα στην παρθένα ακτογραμμή των Συβότων, τα δύο αποκλειστικά μας διαμερίσματα — Ήρα και Δίας — προσφέρουν μια ασύγκριτη εμπειρία μεσογειακής πολυτέλειας.',
    explore: 'Εξερευνήστε', exploreHera: 'Εξερευνήστε την Ήρα', exploreZeus: 'Εξερευνήστε τον Δία',
    theApartments: 'Τα Διαμερίσματα',
    heraDesc: 'Ονομασμένο από τη βασίλισσα των θεών, το διαμέρισμα Ήρα ενσαρκώνει χάρη και κομψότητα.',
    zeusDesc: 'Εντυπωσιακό και εκλεπτυσμένο, το διαμέρισμα Δίας αποτυπώνει το πνεύμα του βασιλιά του Ολύμπου.',
    guestExperiences: 'Εμπειρίες Επισκεπτών', whatGuestsSay: 'Τι Λένε οι Επισκέπτες μας',
    theExperience: 'Η Εμπειρία', whySivotaRoyal: 'Γιατί Sivota Royal',
    privateLuxury: 'Ιδιωτική Πολυτέλεια', stunningViews: 'Εκπληκτική Θέα',
    nearBeaches: 'Κοντά στις Παραλίες', mediterraneanLife: 'Μεσογειακή Ζωή',
    discover: 'Ανακαλύψτε', sivotaBeyond: 'Σύβοτα & Πέρα',
    sivotaBeyondDesc: 'Εξερευνήστε κρυμμένες παραλίες, γοητευτικά λιμάνια και αυθεντικές ελληνικές ταβέρνες.',
    exploreDestinations: 'Εξερευνήστε Προορισμούς',
    location: 'Τοποθεσία', findUs: 'Βρείτε μας',
    planEscape: 'Σχεδιάστε την Απόδρασή σας στα Σύβοτα',
    planEscapeDesc: 'Ζήστε χαλαρή μεσογειακή ζωή στο Sivota Royal.',
    checkAvailability: 'Ελέγξτε Διαθεσιμότητα', sendEnquiry: 'Στείλτε Αίτημα',
    quickLinks: 'Γρήγοροι Σύνδεσμοι', bookNow: 'Κρατήστε Τώρα',
    footerDesc: 'Πολυτελή διαμερίσματα στην καρδιά των Συβότων, Ελλάδα.',
    allRights: 'Με επιφύλαξη παντός δικαιώματος.',
    overview: 'Επισκόπηση', amenities: 'Παροχές', gallery: 'Γκαλερί',
    graceElegance: 'Χάρη & Κομψότητα', powerRefinement: 'Δύναμη & Εκλέπτυνση',
    everythingYouNeed: 'Όλα Όσα Χρειάζεστε',
    readyExperience: 'Έτοιμοι να Ζήσετε',
    ourStory: 'Η Ιστορία μας', philosophy: 'Φιλοσοφία',
    craftedTitle: 'Δημιουργημένο για Εκείνους που Αναζητούν Περισσότερα',
    medLiving: 'Μεσογειακή Διαβίωση', beautyOfSivota: 'Η Ομορφιά των Συβότων',
    theResidences: 'Οι Κατοικίες', ourApartments: 'Τα Διαμερίσματά μας',
    testimonials: 'Μαρτυρίες', guestReviews: 'Κριτικές Επισκεπτών',
    basedOnReviews: 'Βασισμένο σε κριτικές επισκεπτών',
    reserve: 'Κράτηση', bookYourStay: 'Κλείστε τη Διαμονή σας',
    option1: 'Επιλογή 1', option2: 'Επιλογή 2',
    sendAnEnquiry: 'Στείλτε Αίτημα', bookDirectly: 'Κρατήστε Απευθείας',
    fullName: 'Ονοματεπώνυμο', email: 'Email', phone: 'Τηλέφωνο',
    checkin: 'Άφιξη', checkout: 'Αναχώρηση', guests: 'Αριθμός Επισκεπτών',
    message: 'Μήνυμα', sendMessage: 'Αποστολή Μηνύματος',
    bookDirectlyDesc: 'Για άμεση κράτηση, επισκεφθείτε τη σελίδα μας στο Booking.com.',
    getInTouch: 'Επικοινωνήστε', contactDetails: 'Στοιχεία Επικοινωνίας',
    sivotaGreece: 'Σύβοτα, Θεσπρωτία, Ελλάδα',
    destinationsIntro: 'Τα Σύβοτα και τα περίχωρα προσφέρουν πλήθος εμπειριών — από παρθένες παραλίες μέχρι αυθεντική ελληνική ζωή.',
    whatsappMessage: 'Γεια σας! Ενδιαφέρομαι για κράτηση στο Sivota Royal Luxury Retreat.',

    // NEW KEYS
    sivotaIonianGreece: 'Σύβοτα · Ιόνια Ακτή · Ελλάδα',
    whereSeaMeetsSky: 'Εκεί που η Θάλασσα Συναντά τον Ουρανό',
    heroLongDesc: 'Δύο πολυτελή πέτρινα διαμερίσματα πάνω από το Ιόνιο Πέλαγος — ξυπνήστε με θέα την Κέρκυρα, κοιμηθείτε με ηλιοβασιλέματα',
    viewApartments: 'Δείτε τα Διαμερίσματα',
    ratingLabel: 'Βαθμολογία 9.2',
    exceptionalBooking: 'Εξαιρετικό στο Booking.com',
    onBookingCom: '9.2 στο Booking.com',
    hilltopViews: '🏔️ Πανοραμική Θέα',
    twoMinBeach: '🌊 2 λεπτά από την Παραλία',
    privatePool: '🏊 Ιδιωτική Πισίνα',
    instantConfirmation: 'Άμεση Επιβεβαίωση',
    bookWithConfidence: 'Κρατήστε με σιγουριά',
    freeCancellation: 'Δωρεάν Ακύρωση',
    flexibleBooking: 'Ευέλικτες επιλογές κράτησης',
    whereMedComesAlive: 'Εκεί που η Μεσόγειος Ζωντανεύει',
    indexIntroDesc: 'Φωλιασμένα στην κορυφή ενός γραφικού λόφου στο γοητευτικό χωριό των Συβότων, τα πέτρινα διαμερίσματά μας προσφέρουν πανοραμική θέα στο Ιόνιο Πέλαγος και το νησί της Κέρκυρας.',
    heraPanoramicDesc: 'Ένα πανοραμικό καταφύγιο με απεριόριστη θέα στο Ιόνιο Πέλαγος και το νησί της Κέρκυρας.',
    zeusPoolDesc: 'Ένα πολυτελές καταφύγιο με εντυπωσιακή ιδιωτική πισίνα και πανοραμική θέα στο Ιόνιο.',
    seaViewsTerrace: 'Θέα θαλάσσης & βεράντα',
    privatePoolCourtyard: 'Ιδιωτική πισίνα & αυλή',
    twoBeds: '2 Κρεβάτια', seaViews: 'Θέα Θάλασσα', wiFi: 'Wi-Fi', ac: 'Κλιματισμός',
    kitchen: 'Κουζίνα', terrace: 'Βεράντα', pool: 'Πισίνα', views: 'Θέα',
    twoBedrooms: '2 Υπνοδωμάτια', freeWiFi: 'Δωρεάν Wi-Fi', fullKitchen: 'Πλήρης Κουζίνα',
    privatePoolLabel: 'Ιδιωτική Πισίνα', panoramicViews: 'Πανοραμική Θέα',
    seaCorfuViews: 'Θέα Θάλασσα & Κέρκυρα', privateTerrace: 'Ιδιωτική Βεράντα',
    airConditioning: 'Κλιματισμός', highSpeedWiFi: 'Wi-Fi Υψηλής Ταχύτητας',
    smartTV: 'Smart TV', washingMachine: 'Πλυντήριο',
    stoneVeranda: 'Πέτρινη Βεράντα', panoramicSeaViews: 'Πανοραμική Θέα Θάλασσας',
    bestViewsQuote: '«Η καλύτερη θέα σε ολόκληρα τα Σύβοτα»',
    ourGuests: '— Οι Επισκέπτες μας',
    medLivingFinest: 'Μεσογειακή Ζωή στο Απόγειό της',
    sunDrenchedTerraces: 'Ηλιόλουστες Βεράντες',
    sunDrenchedDesc: 'Πρωινό στον ήλιο, δείπνο κάτω από τα αστέρια',
    endlessBlueViews: 'Ατελείωτο Γαλάζιο',
    endlessBlueDesc: 'Πανοραμική θέα στο Ιόνιο Πέλαγος και την Κέρκυρα',
    crystalClearBeaches: 'Κρυστάλλινες Παραλίες',
    crystalClearDesc: 'Κρυφοί κόλποι και τιρκουάζ νερά λίγα λεπτά μακριά',
    authenticGreekLife: 'Αυθεντική Ελληνική Ζωή',
    authenticGreekDesc: 'Ταβέρνες, λιμάνια και περιπέτειες νησιών',
    sunSeaStone: 'Ήλιος, Θάλασσα & Πέτρα',
    islandHoppingFromSivota: 'Νησιοπλοΐα από τα Σύβοτα',
    islandHoppingDesc: 'Εξερευνήστε τα στολίδια του Ιονίου — όλα σε κοντινή απόσταση',
    readAllReviews: 'Διαβάστε όλες τις κριτικές',
    startPlanning: 'Ξεκινήστε τον Σχεδιασμό',
    yourMedEscape: 'Η Μεσογειακή σας Απόδραση σας Περιμένει',
    bookDirectlyBestRates: 'Κρατήστε απευθείας για τις καλύτερες τιμές και προσωπική υποδοχή από τον Δημήτρη',
    yourStoryStartsHere: 'Η Ιστορία σας Ξεκινά Εδώ',
    readyExperienceSivotaRoyal: 'Έτοιμοι να Ζήσετε το Sivota Royal;',
    readyExperienceSivota: 'Έτοιμοι να Ζήσετε τα Σύβοτα;',
    bookDirectlyBestRatesDimitris: 'Κρατήστε απευθείας για τις καλύτερες τιμές',
    builtWithLove: 'Χτισμένο με Αγάπη, Λουσμένο στο Φως',
    notJustPlace: 'Αυτό δεν είναι Απλά ένα Μέρος για Διαμονή',
    aboutIntro1: 'Φανταστείτε να ξυπνάτε με τον ήχο της θάλασσας, να βγαίνετε σε μια ζεστή βεράντα και να βλέπετε το φως να χορεύει πάνω από το Ιόνιο προς την Κέρκυρα.',
    aboutIntro2: 'Το Sivota Royal δεν σχεδιάστηκε σε γραφείο — χτίστηκε με τα χέρια, πέτρα πέτρα, από μια οικογένεια που πιστεύει ότι οι καλύτερες διακοπές γίνονται σε μέρη φτιαγμένα με αγάπη.',
    whatSetsUsApart: 'Τι μας Ξεχωρίζει',
    moreThanAccommodation: 'Κάτι Παραπάνω από Κατάλυμα',
    handcrafted: 'Χειροποίητο',
    handcraftedDesc: 'Κάθε πέτρα τοποθετημένη με τα χέρια, κάθε λεπτομέρεια μελετημένη.',
    bestViews: 'Καλύτερη Θέα',
    bestViewsDesc: 'Βεράντες στην κορυφή του λόφου με αδιάκοπο πανόραμα στο Ιόνιο.',
    twoMinToBeach: '2 Λεπτά από την Παραλία',
    twoMinToBeachDesc: 'Κρυστάλλινα νερά λίγα βήματα μακριά.',
    authentic: 'Αυθεντικό',
    authenticDesc: 'Χωρίς πλήθη, χωρίς βιασύνη. Ο ρυθμός της ελληνικής ζωής.',
    theSetting: 'Το Περιβάλλον',
    everyMorningGift: 'Κάθε Πρωί Μοιάζει με Δώρο',
    settingDesc1: 'Χρυσό φως πλημμυρίζει τα παράθυρα. Βγαίνετε στη βεράντα με τον ήχο της θάλασσας.',
    settingDesc2: 'Αυτό δεν είναι όνειρο — αυτό είναι το πρωί σας στο Sivota Royal.',
    greeceBestKeptSecret: 'Το Καλύτερα Κρυμμένο Μυστικό της Ελλάδας',
    locationDesc1: 'Τα Σύβοτα είναι ένα κρυμμένο στολίδι στην ακτή της Θεσπρωτίας.',
    locationDesc2: 'Εξερευνήστε κρυφούς κόλπους, δειπνήστε στο λιμάνι ή απολαύστε το ηλιοβασίλεμα.',
    quoteNotHotel: '«Δεν χτίσαμε ξενοδοχείο. Χτίσαμε το μέρος που θα θέλαμε να μείνουμε.»',
    ownerName: '— Δημήτρης Βλάχος, Ιδιοκτήτης',
    theProperty: 'Η Ιδιοκτησία',
    everyDetailConsidered: 'Κάθε Λεπτομέρεια, Μελετημένη',
    personalWelcome: 'Μια Προσωπική Υποδοχή',
    meetDimitris: 'Γνωρίστε τον Δημήτρη',
    dimitrisQuote1: '«Είμαι ο Δημήτρης και το Sivota Royal είναι το έργο ζωής μου. Από μικρός δούλεψα δίπλα στον πατέρα μου, βάζοντας κάθε πέτρα, φυτεύοντας κάθε ελιά.»',
    dimitrisQuote2: '«Όταν φτάσετε, θα είμαι εδώ να σας υποδεχτώ προσωπικά. Θα σας δείξω τις καλύτερες κρυμμένες παραλίες.»',
    yourHost: 'Ο Οικοδεσπότης σας',
    dimitrisMadeFeel: '«Ο Δημήτρης μας έκανε να νιώσουμε σαν οικογένεια»',
    twoExceptionalResidences: 'Δύο εξαιρετικές κατοικίες, η καθεμία με τον δικό της χαρακτήρα.',
    heraExtendedDesc: 'Αυτό το εκπληκτικό πανοραμικό διαμέρισμα προσφέρει απεριόριστη θέα στο Ιόνιο Πέλαγος.',
    zeusExtendedDesc: 'Ένα πολυτελές διαμέρισμα με εντυπωσιακή ιδιωτική πισίνα.',
    stayDetails: 'Λεπτομέρειες Διαμονής',
    yourDetails: 'Τα Στοιχεία σας',
    selectYourDates: 'Επιλέξτε ημερομηνίες',
    total: 'Σύνολο', discount: 'Έκπτωση',
    couponCode: 'Κωδικός Κουπονιού', enterCode: 'Εισάγετε κωδικό', apply: 'Εφαρμογή',
    continueBtn: 'Συνέχεια', back: 'Πίσω', sending: 'Αποστολή...',
    yourSelection: 'Η Επιλογή σας', editSelection: 'Επεξεργασία επιλογής',
    apartment: 'Διαμέρισμα',
    chatWithUs: 'Επικοινωνήστε Απευθείας',
    chatWithUsDesc: 'Προτιμάτε μήνυμα; Επικοινωνήστε μαζί μας στο WhatsApp.',
    chatOnWhatsApp: '💬 Συνομιλία στο WhatsApp',
    noPricingSet: 'Δεν έχει οριστεί τιμή — θα επιβεβαιώσουμε.',
    minimumStay: 'Ελάχιστη διαμονή {min} βράδια',
    thankYouEnquiry: 'Ευχαριστούμε! Το αίτημά σας στάλθηκε.',
    fillNameEmail: 'Συμπληρώστε όνομα και email.',
    selectApartment: 'Επιλέξτε διαμέρισμα.',
    selectDates: 'Επιλέξτε ημερομηνίες.',
    invalidCoupon: 'Μη έγκυρος κωδικός κουπονιού',
    viewOnBooking: 'Δείτε στο Booking.com',
    fillRequiredFields: 'Συμπληρώστε όλα τα υποχρεωτικά πεδία.',
    messageSent: 'Το μήνυμα στάλθηκε! Θα σας απαντήσουμε σύντομα.',
    joinOurGuests: 'Γίνετε Φιλοξενούμενοί μας',
    createYourStory: 'Δημιουργήστε τη Δική σας Ιστορία',
    featuredQuote: '«Η καλύτερη θέα σε ολόκληρα τα Σύβοτα — μια πραγματικά αξέχαστη εμπειρία»',
    featuredQuoteAuthor: '— Μαρία Κ., Αθήνα',
    hiddenCovesDesc: 'Κρυφοί κόλποι, περιπέτειες στα νησιά και αυθεντικές ελληνικές εμπειρίες — όλα από το κατώφλι σας',
    secludedBeaches: 'Απομονωμένες Παραλίες',
    secludedBeachesDesc: 'Κρυστάλλινα νερά και λευκά βότσαλα κατά μήκος της ακτογραμμής.',
    sivotaCoastline: 'Ακτογραμμή Συβότων',
    boatTrips: 'Βόλτες με Σκάφος',
    boatTripsDesc: 'Εξερευνήστε τα Ιόνια νησιά και κρυμμένες θαλάσσιες σπηλιές.',
    ionianSea: 'Ιόνιο Πέλαγος',
    sivotaHarbour: 'Λιμάνι Συβότων',
    sivotaHarbourDesc: 'Περπατήστε κατά μήκος της προκυμαίας και απολαύστε φρέσκα θαλασσινά.',
    sivotaVillage: 'Χωριό Σύβοτα',
    localDining: 'Τοπική Γαστρονομία',
    localDiningDesc: 'Από παραθαλάσσιες ταβέρνες μέχρι εστιατόρια στον λόφο.',
    sivotaSurrounds: 'Σύβοτα & Περίχωρα',
    islandHopping: 'Νησιοπλοΐα',
    jewelsOfIonian: 'Τα Στολίδια του Ιονίου',
    islandHoppingIntro: 'Από τα Σύβοτα, εξερευνήστε τρία από τα ομορφότερα νησιά της Μεσογείου',
    corfuDesc: 'Παλιά Πόλη UNESCO, Αχίλλειο Ανάκτορο και εκπληκτικές παραλίες.',
    corfuTime: '1,5 ώρα με πλοίο',
    paxosDesc: 'Φημισμένο για θαλάσσιες σπηλιές, τιρκουάζ νερά και γοητευτικά χωριά.',
    paxosTime: '1 ώρα με σκάφος',
    antipaxosDesc: 'Βουτουμί και Βρίκα — δύο από τις ομορφότερες παραλίες της Ελλάδας.',
    antipaxosTime: '1,5 ώρα με σκάφος',
    thesprotiaRegion: 'Περιοχή Θεσπρωτίας',
    natureHiking: 'Φύση & Πεζοπορία',
    natureHikingDesc1: 'Εξερευνήστε ελαιώνες, αρχαία μονοπάτια και πράσινες πλαγιές.',
    natureHikingDesc2: 'Από ορεινά χωριά μέχρι παραθαλάσσια μονοπάτια.',
    yourGateway: 'Η Πύλη σας',
    startIonianAdventure: 'Ξεκινήστε την Ιόνια Περιπέτεια',
    ionianAdventureDesc: 'Από τα Σύβοτα, εξερευνήστε Κέρκυρα, Παξούς και Αντίπαξους.',
    heraHeroDesc: 'Η καλύτερη θέα των Συβότων — ατελείωτο γαλάζιο και ηλιοβασιλέματα στην Κέρκυρα',
    masterBedroom: 'Κύριο Υπνοδωμάτιο',
    heraMasterTitle: 'Ονειρικό Υπνοδωμάτιο με Ασύγκριτα Ηλιοβασιλέματα',
    heraMasterDesc: 'Αυτό το εκπληκτικό πανοραμικό υπνοδωμάτιο προσφέρει απεριόριστη θέα στο Ιόνιο Πέλαγος.',
    secondaryBedroom: 'Δεύτερο Υπνοδωμάτιο',
    heraSecondaryTitle: 'Γοητευτικό Υπνοδωμάτιο με Θέα Θάλασσα',
    heraSecondaryDesc: 'Αυτό το υπέροχο δεύτερο υπνοδωμάτιο φιλοξενεί άνετα μέχρι τρεις επισκέπτες.',
    kitchenDining: 'Κουζίνα & Τραπεζαρία',
    heraKitchenTitle: 'Πολυτελής Κουζίνα με Παραδοσιακό Ελληνικό Σχεδιασμό',
    heraKitchenDesc: 'Αυτή η πολυτελής κουζίνα διαθέτει εκπληκτικό σχεδιασμό από πέτρα και ξύλο.',
    livingRoom: 'Σαλόνι',
    heraLivingTitle: 'Βασιλικό Σαλόνι με Παραδοσιακή Κομψότητα',
    heraLivingDesc: 'Αυτό το μεγαλοπρεπές σαλόνι αποπνέει βασιλική αίγλη με παραδοσιακά έπιπλα.',
    secureYourDates: 'Κλείστε τις ημερομηνίες σας στο λοφώδες καταφύγιό μας.',
    exceptionalOnBooking: 'Εξαιρετικό · Booking.com',
    heraOverviewDesc1: 'Ονομασμένο από τη βασίλισσα των θεών, το διαμέρισμα Ήρα ενσαρκώνει χάρη, ζεστασιά και αιώνια ομορφιά.',
    heraOverviewDesc2: 'Ξυπνήστε με τον ήχο των κυμάτων, απολαύστε τον πρωινό καφέ στη βεράντα.',
    zeusHeroDesc: 'Ιδιωτική πισίνα, πανοραμική θέα στο Ιόνιο και παραδοσιακή πέτρινη τεχνοτροπία',
    zeusOverviewDesc1: 'Φέροντας το όνομα του βασιλιά των θεών, το διαμέρισμα Δίας εντυπωσιάζει.',
    zeusOverviewDesc2: 'Ανακαλύψτε τη μαγευτική αυλή με την πισίνα και τα κομψά ξαπλώστρα.',
    courtyardPool: 'Αυλή & Πισίνα',
    zeusCourtTitle: 'Ειδυλλιακή Αυλή με Πισίνα και Πανοραμική Θέα',
    zeusCourtDesc: 'Ανακαλύψτε τη μαγευτική αυλή — μια πράσινη όαση με πισίνα και μπουκαμβίλιες.',
    zeusMasterTitle: 'Γοητευτικό Κύριο Υπνοδωμάτιο με Παραδοσιακά Ξύλινα Έπιπλα',
    zeusMasterDesc: 'Βήμα στο μαγευτικό υπνοδωμάτιο με χειροποίητα ξύλινα έπιπλα.',
    twinBedroom: 'Δίκλινο Υπνοδωμάτιο',
    zeusTwinTitle: 'Άνετο Δίκλινο Υπνοδωμάτιο με Μπαλκόνι & Θέα',
    zeusTwinDesc: 'Με δύο μονά κρεβάτια, αυτό το δωμάτιο εξασφαλίζει ξεκούραστη διαμονή.',
    stoneVerandaLabel: 'Πέτρινη Βεράντα',
    zeusVerandaTitle: 'Παραδοσιακή Πέτρινη Βεράντα με Πανοραμική Θέα',
    zeusVerandaDesc: 'Η εξαιρετική βεράντα από χειροποίητη πέτρα με ξύλινη στέγη.',
    secureYourDatesZeus: 'Κλείστε τις ημερομηνίες σας για πισίνα και πανοραμική θέα.',
    seaViewBedrooms: 'Υπνοδωμάτια με θέα', premiumBedLinens: 'Πολυτελή σεντόνια',
    fullyEquippedKitchen: 'Πλήρως εξοπλισμένη κουζίνα', privateTerraceAmenity: 'Ιδιωτική βεράντα',
    airConditioningAmenity: 'Κλιματισμός', highSpeedWiFiAmenity: 'Wi-Fi υψηλής ταχύτητας',
    smartTVAmenity: 'Smart TV', washingMachineAmenity: 'Πλυντήριο',
    hairDryer: 'Πιστολάκι', ironIroningBoard: 'Σίδερο & σιδερώστρα',
    toiletries: 'Είδη μπάνιου', beachTowels: 'Πετσέτες θαλάσσης',
    stoneBuiltDesign: 'Πέτρινο σχέδιο', woodenDetails: 'Ξύλινες λεπτομέρειες',
    traditionalArchitecture: 'Παραδοσιακή αρχιτεκτονική', modernComforts: 'Σύγχρονες ανέσεις',
    privatePoolAmenity: 'Ιδιωτική πισίνα', panoramicSeaViewsAmenity: 'Πανοραμική θέα',
    spaciousCourtyard: 'Ευρύχωρη αυλή',
    stoneBuiltVeranda: 'Πέτρινη βεράντα', woodenFurnishings: 'Ξύλινα έπιπλα',
    bougainvilleaPergola: 'Πέργκολα μπουκαμβίλιας', sunLoungers: 'Ξαπλώστρες',
    weValuePrivacy: 'Σεβόμαστε το Απόρρητό σας',
    cookieDesc: 'Χρησιμοποιούμε απαραίτητα cookies για τη σωστή λειτουργία του ιστοτόπου. Δεν χρησιμοποιούνται cookies παρακολούθησης.',
    decline: 'Απόρριψη',
    accept: 'Αποδοχή',

    // Admin Dashboard
    adminDashboard: 'Πίνακας Διαχείρισης',
    dashboardOverview: 'Επισκόπηση Πίνακα',
    enquiries: 'Αιτήματα',
    bookings: 'Κρατήσεις',
    seasonsPricing: 'Εποχές & Τιμολόγηση',
    coupons: 'Κουπόνια',
    photos: 'Φωτογραφίες',
    revenue: 'Έσοδα',
    signOut: 'Αποσύνδεση',
    pendingEnquiries: 'Εκκρεμή Αιτήματα',
    totalBookings: 'Σύνολο Κρατήσεων',
    totalRevenue: 'Συνολικά Έσοδα',
    depositsReceived: 'Προκαταβολές',
    remainingBalance: 'Υπόλοιπο',
    totalEnquiries: 'Σύνολο Αιτημάτων',
    all: 'Όλα',
    pending: 'Εκκρεμή',
    accepted: 'Αποδεκτά',
    declined: 'Απορριφθέντα',
    adminAccept: 'Αποδοχή',
    adminDecline: 'Απόρριψη',
    edit: 'Επεξεργασία',
    delete: 'Διαγραφή',
    save: 'Αποθήκευση',
    cancel: 'Ακύρωση',
    noEnquiriesFound: 'Δεν βρέθηκαν αιτήματα',
    submitted: 'Υποβλήθηκε',
    nights: 'Βράδια',
    coupon: 'Κουπόνι',
    upcoming: 'Επερχόμενες',
    past: 'Παρελθούσες',
    unpaid: 'Απλήρωτο',
    depositReceived: 'Προκαταβολή',
    fullyPaid: 'Πλήρως Πληρωμένο',
    noUpcomingBookings: 'Δεν υπάρχουν επερχόμενες κρατήσεις',
    noPastBookings: 'Δεν υπάρχουν παρελθούσες κρατήσεις',
    name: 'Όνομα',
    emailLabel: 'Email',
    phoneLabel: 'Τηλέφωνο',
    apartmentLabel: 'Διαμέρισμα',
    dates: 'Ημερομηνίες',
    guestsLabel: 'Επισκέπτες',
    totalAmount: 'Συνολικό Ποσό',
    depositPaid: 'Προκαταβολή (€)',
    paymentStatus: 'Κατάσταση Πληρωμής',
    notes: 'Σημειώσεις',
    bookingUpdated: 'Η κράτηση ενημερώθηκε',
    bookingDeleted: 'Η κράτηση διαγράφηκε',
    deleteBookingConfirm: 'Διαγραφή κράτησης για {name}; Δεν μπορεί να αναιρεθεί.',
    addNotesPlaceholder: 'Προσθέστε σημειώσεις...',
    notesSaved: 'Οι σημειώσεις αποθηκεύτηκαν',
    activeSeason: 'Ενεργή Εποχή',
    pricingPeriods: 'Περίοδοι Τιμολόγησης',
    activeSeasonDesc: 'Ορίστε τη συνολική ενεργή εποχή. Μόνο ημερομηνίες εντός αυτής θα είναι διαθέσιμες στους επισκέπτες.',
    pricingPeriodsDesc: 'Δημιουργήστε περιόδους τιμολόγησης. Κάθε περίοδος ορίζει τιμές ανά βράδια και ελάχιστη διαμονή.',
    setActiveSeason: 'Ορισμός Ενεργής Εποχής',
    addPeriod: 'Προσθήκη Περιόδου',
    dateRange: 'Εύρος Ημερομηνιών',
    selectDatesAdmin: 'Επιλέξτε ημερομηνίες',
    minStay: 'Ελάχ. Διαμονή (βράδια)',
    heraPriceNight: 'Τιμή Ήρα/Βράδυ (€)',
    zeusPriceNight: 'Τιμή Δίας/Βράδυ (€)',
    create: 'Δημιουργία',
    update: 'Ενημέρωση',
    inactive: 'Ανενεργό',
    noPricingPeriods: 'Δεν έχουν ρυθμιστεί περίοδοι τιμολόγησης',
    fillRequired: 'Συμπληρώστε όλα τα υποχρεωτικά πεδία',
    updatedSuccessfully: 'Ενημερώθηκε επιτυχώς',
    createdSuccessfully: 'Δημιουργήθηκε επιτυχώς',
    deleted: 'Διαγράφηκε',
    enquiryDeclinedDeleted: 'Το αίτημα απορρίφθηκε & διαγράφηκε',
    enquiryDeleted: 'Το αίτημα διαγράφηκε',
    enquiryUpdated: 'Το αίτημα ενημερώθηκε',
    bookingCreatedFor: 'Κράτηση δημιουργήθηκε για {name}',
    failedToCreate: 'Αποτυχία δημιουργίας κράτησης',
    failedToSave: 'Αποτυχία αποθήκευσης',
    failedToDelete: 'Αποτυχία διαγραφής',
    failedToUpdate: 'Αποτυχία ενημέρωσης',
    declineDeleteConfirm: 'Απόρριψη και διαγραφή αυτού του αιτήματος;',
    deleteEnquiryConfirm: 'Διαγραφή αιτήματος από {name}; Δεν μπορεί να αναιρεθεί.',
    created: 'Δημιουργήθηκε',
    enquiryId: 'ID Αιτήματος',
  },
  it: {
    home: 'Home', about: 'Chi Siamo', apartments: 'Appartamenti', destinations: 'Destinazioni',
    reviews: 'Recensioni', booking: 'Prenotazione', contact: 'Contatti',
    heroTitle: 'Sivota Royal', heroSubtitle: 'Ritiro di Lusso',
    heroDesc: 'Due residenze esclusive progettate per indimenticabili fughe mediterranee.',
    welcome: 'Benvenuti', welcomeTitle: 'Dove il Lusso Incontra il Mar Ionio',
    welcomeDesc: 'Situati sulla costa incontaminata di Sivota, i nostri due esclusivi appartamenti — Hera e Zeus — offrono un\'esperienza impareggiabile di lusso mediterraneo.',
    explore: 'Esplora', exploreHera: 'Esplora Hera', exploreZeus: 'Esplora Zeus',
    theApartments: 'Gli Appartamenti',
    heraDesc: 'Ispirato alla regina degli dei, l\'appartamento Hera incarna grazia ed eleganza.',
    zeusDesc: 'Imponente e raffinato, l\'appartamento Zeus cattura lo spirito del re dell\'Olimpo.',
    guestExperiences: 'Esperienze degli Ospiti', whatGuestsSay: 'Cosa Dicono i Nostri Ospiti',
    theExperience: 'L\'Esperienza', whySivotaRoyal: 'Perché Sivota Royal',
    privateLuxury: 'Lusso Privato', stunningViews: 'Viste Mozzafiato',
    nearBeaches: 'Vicino alle Spiagge', mediterraneanLife: 'Vita Mediterranea',
    discover: 'Scopri', sivotaBeyond: 'Sivota e Oltre',
    sivotaBeyondDesc: 'Esplora spiagge nascoste, porti affascinanti e autentiche taverne greche.',
    exploreDestinations: 'Esplora Destinazioni',
    location: 'Posizione', findUs: 'Trovaci',
    planEscape: 'Pianifica la Tua Fuga a Sivota',
    planEscapeDesc: 'Vivi il rilassato stile di vita mediterraneo al Sivota Royal.',
    checkAvailability: 'Verifica Disponibilità', sendEnquiry: 'Invia Richiesta',
    quickLinks: 'Link Rapidi', bookNow: 'Prenota Ora',
    footerDesc: 'Appartamenti di lusso nel cuore di Sivota, Grecia.',
    allRights: 'Tutti i diritti riservati.',
    overview: 'Panoramica', amenities: 'Servizi', gallery: 'Galleria',
    graceElegance: 'Grazia & Eleganza', powerRefinement: 'Potenza & Raffinatezza',
    everythingYouNeed: 'Tutto Ciò di Cui Hai Bisogno',
    readyExperience: 'Pronti a Vivere',
    ourStory: 'La Nostra Storia', philosophy: 'Filosofia',
    craftedTitle: 'Creato per Chi Cerca di Più',
    medLiving: 'Vita Mediterranea', beautyOfSivota: 'La Bellezza di Sivota',
    theResidences: 'Le Residenze', ourApartments: 'I Nostri Appartamenti',
    testimonials: 'Testimonianze', guestReviews: 'Recensioni degli Ospiti',
    basedOnReviews: 'Basato sulle recensioni degli ospiti',
    reserve: 'Prenota', bookYourStay: 'Prenota il Tuo Soggiorno',
    option1: 'Opzione 1', option2: 'Opzione 2',
    sendAnEnquiry: 'Invia Richiesta', bookDirectly: 'Prenota Direttamente',
    fullName: 'Nome Completo', email: 'Indirizzo Email', phone: 'Numero di Telefono',
    checkin: 'Check-in', checkout: 'Check-out', guests: 'Numero di Ospiti',
    message: 'Messaggio', sendMessage: 'Invia Messaggio',
    bookDirectlyDesc: 'Per prenotazioni immediate, visita la nostra pagina Booking.com.',
    getInTouch: 'Contattaci', contactDetails: 'Dettagli di Contatto',
    sivotaGreece: 'Sivota, Thesprotia, Grecia',
    destinationsIntro: 'Sivota e i suoi dintorni offrono una ricchezza di esperienze.',
    whatsappMessage: 'Ciao! Sono interessato a prenotare al Sivota Royal Luxury Retreat.',

    sivotaIonianGreece: 'Sivota · Costa Ionica · Grecia',
    whereSeaMeetsSky: 'Dove il Mare Incontra il Cielo',
    heroLongDesc: 'Due appartamenti di lusso in pietra affacciati sul Mar Ionio — svegliatevi con Corfù, addormentatevi con i tramonti',
    viewApartments: 'Vedi Appartamenti',
    ratingLabel: 'Valutazione 9.2', exceptionalBooking: 'Eccezionale su Booking.com',
    onBookingCom: '9.2 su Booking.com',
    hilltopViews: '🏔️ Viste Panoramiche', twoMinBeach: '🌊 2 min dalla Spiaggia', privatePool: '🏊 Piscina Privata',
    instantConfirmation: 'Conferma Immediata', bookWithConfidence: 'Prenota con fiducia',
    freeCancellation: 'Cancellazione Gratuita', flexibleBooking: 'Opzioni di prenotazione flessibili',
    whereMedComesAlive: 'Dove il Mediterraneo Prende Vita',
    indexIntroDesc: 'In cima a una pittoresca collina nel villaggio di Sivota, i nostri appartamenti in pietra offrono viste panoramiche sul Mar Ionio e l\'isola di Corfù.',
    heraPanoramicDesc: 'Un santuario panoramico con viste illimitate sul Mar Ionio e l\'isola di Corfù.',
    zeusPoolDesc: 'Un rifugio di lusso con piscina privata e viste panoramiche sullo Ionio.',
    seaViewsTerrace: 'Vista mare & terrazza', privatePoolCourtyard: 'Piscina privata & cortile',
    twoBeds: '2 Letti', seaViews: 'Vista Mare', wiFi: 'Wi-Fi', ac: 'A/C',
    kitchen: 'Cucina', terrace: 'Terrazza', pool: 'Piscina', views: 'Viste',
    twoBedrooms: '2 Camere', freeWiFi: 'Wi-Fi Gratuito', fullKitchen: 'Cucina Completa',
    privatePoolLabel: 'Piscina Privata', panoramicViews: 'Viste Panoramiche',
    seaCorfuViews: 'Vista Mare & Corfù', privateTerrace: 'Terrazza Privata',
    airConditioning: 'Aria Condizionata', highSpeedWiFi: 'Wi-Fi ad Alta Velocità',
    smartTV: 'Smart TV', washingMachine: 'Lavatrice',
    stoneVeranda: 'Veranda in Pietra', panoramicSeaViews: 'Viste Panoramiche sul Mare',
    bestViewsQuote: '"Le migliori viste di tutta Sivota"', ourGuests: '— I Nostri Ospiti',
    medLivingFinest: 'Vita Mediterranea al suo Meglio',
    sunDrenchedTerraces: 'Terrazze Soleggiate', sunDrenchedDesc: 'Colazione al sole, cena sotto le stelle',
    endlessBlueViews: 'Viste Blu Infinite', endlessBlueDesc: 'Panorama sullo Ionio e Corfù',
    crystalClearBeaches: 'Spiagge Cristalline', crystalClearDesc: 'Baie nascoste e acque turchesi a pochi minuti',
    authenticGreekLife: 'Vita Greca Autentica', authenticGreekDesc: 'Taverne, porti e avventure tra le isole',
    sunSeaStone: 'Sole, Mare & Pietra',
    islandHoppingFromSivota: 'Island Hopping da Sivota',
    islandHoppingDesc: 'Esplora i gioielli del Mar Ionio — tutti a portata di mano',
    readAllReviews: 'Leggi tutte le recensioni',
    startPlanning: 'Inizia a Pianificare',
    yourMedEscape: 'La Tua Fuga Mediterranea Ti Aspetta',
    bookDirectlyBestRates: 'Prenota direttamente per le migliori tariffe e un benvenuto personale da Dimitris',
    yourStoryStartsHere: 'La Tua Storia Inizia Qui',
    readyExperienceSivotaRoyal: 'Pronti a Vivere Sivota Royal?',
    readyExperienceSivota: 'Pronti a Vivere Sivota?',
    bookDirectlyBestRatesDimitris: 'Prenota direttamente per le migliori tariffe',
    builtWithLove: 'Costruito con Amore, Bagnato dalla Luce',
    notJustPlace: 'Non è Solo un Posto dove Stare',
    aboutIntro1: 'Immagina di svegliarti al suono del mare, uscire su una terrazza soleggiata e guardare la luce danzare sullo Ionio.',
    aboutIntro2: 'Sivota Royal non è stato progettato in un ufficio — è stato costruito a mano, pietra su pietra.',
    whatSetsUsApart: 'Cosa Ci Distingue', moreThanAccommodation: 'Più di un Alloggio',
    handcrafted: 'Artigianale', handcraftedDesc: 'Ogni pietra posata a mano, ogni dettaglio curato.',
    bestViews: 'Viste Migliori', bestViewsDesc: 'Terrazze panoramiche sul Mar Ionio e Corfù.',
    twoMinToBeach: '2 Min dalla Spiaggia', twoMinToBeachDesc: 'Acque cristalline a pochi passi.',
    authentic: 'Autentico', authenticDesc: 'Nessuna folla. Il ritmo della vita costiera greca.',
    theSetting: 'L\'Ambiente', everyMorningGift: 'Ogni Mattina Sembra un Regalo',
    settingDesc1: 'La luce dorata entra dalle finestre. Esci sulla terrazza privata con il suono del mare.',
    settingDesc2: 'Questo non è un sogno — è la tua mattina al Sivota Royal.',
    greeceBestKeptSecret: 'Il Segreto Meglio Custodito della Grecia',
    locationDesc1: 'Sivota è un gioiello nascosto sulla costa della Thesprotia.',
    locationDesc2: 'Esplora baie nascoste, cena al porto, guarda il tramonto dalla terrazza.',
    quoteNotHotel: '"Non abbiamo costruito un hotel. Abbiamo costruito il posto dove vorremmo stare."',
    ownerName: '— Dimitris Vlachos, Proprietario',
    theProperty: 'La Proprietà', everyDetailConsidered: 'Ogni Dettaglio, Curato',
    personalWelcome: 'Un Benvenuto Personale', meetDimitris: 'Incontra Dimitris',
    dimitrisQuote1: '"Sono Dimitris, e Sivota Royal è il lavoro della mia vita."',
    dimitrisQuote2: '"Quando arriverete, sarò qui ad accogliervi personalmente."',
    yourHost: 'Il Tuo Host', dimitrisMadeFeel: '"Dimitris ci ha fatto sentire come in famiglia"',
    twoExceptionalResidences: 'Due residenze eccezionali, ciascuna con il proprio carattere.',
    heraExtendedDesc: 'Questo splendido appartamento panoramico offre viste illimitate sul Mar Ionio.',
    zeusExtendedDesc: 'Un appartamento di lusso con piscina privata e viste panoramiche.',
    stayDetails: 'Dettagli del Soggiorno', yourDetails: 'I Tuoi Dati',
    selectYourDates: 'Seleziona le date', total: 'Totale', discount: 'Sconto',
    couponCode: 'Codice Coupon', enterCode: 'Inserisci codice', apply: 'Applica',
    continueBtn: 'Continua', back: 'Indietro', sending: 'Invio...',
    yourSelection: 'La Tua Selezione', editSelection: 'Modifica selezione',
    apartment: 'Appartamento',
    chatWithUs: 'Contattaci Direttamente', chatWithUsDesc: 'Preferisci messaggiare? Contattaci su WhatsApp.',
    chatOnWhatsApp: '💬 Chatta su WhatsApp',
    noPricingSet: 'Nessun prezzo impostato — confermeremo la tariffa.',
    minimumStay: 'Soggiorno minimo {min} notti',
    thankYouEnquiry: 'Grazie! La tua richiesta è stata inviata.',
    fillNameEmail: 'Compila nome ed email.', selectApartment: 'Seleziona un appartamento.',
    selectDates: 'Seleziona le date.', invalidCoupon: 'Codice coupon non valido',
    viewOnBooking: 'Vedi su Booking.com',
    fillRequiredFields: 'Compila tutti i campi obbligatori.',
    messageSent: 'Messaggio inviato! Ti risponderemo presto.',
    joinOurGuests: 'Unisciti ai Nostri Ospiti', createYourStory: 'Crea la Tua Storia',
    featuredQuote: '"Le migliori viste di tutta Sivota — un\'esperienza davvero indimenticabile"',
    featuredQuoteAuthor: '— Maria K., Atene',
    hiddenCovesDesc: 'Baie nascoste, avventure tra le isole ed esperienze greche autentiche',
    secludedBeaches: 'Spiagge Appartate', secludedBeachesDesc: 'Acque cristalline e ciottoli bianchi lungo la costa.',
    sivotaCoastline: 'Costa di Sivota', boatTrips: 'Gite in Barca',
    boatTripsDesc: 'Esplora le isole Ionie e grotte marine nascoste.', ionianSea: 'Mar Ionio',
    sivotaHarbour: 'Porto di Sivota', sivotaHarbourDesc: 'Passeggia lungo il porto e gusta pesce fresco.',
    sivotaVillage: 'Villaggio di Sivota', localDining: 'Cucina Locale',
    localDiningDesc: 'Dalle taverne sul mare ai ristoranti in collina.', sivotaSurrounds: 'Sivota e Dintorni',
    islandHopping: 'Island Hopping', jewelsOfIonian: 'Gioielli dello Ionio',
    islandHoppingIntro: 'Da Sivota, esplora tre delle isole più belle del Mediterraneo',
    corfuDesc: 'Centro storico UNESCO, Palazzo Achilleion e spiagge come Paleokastritsa.',
    corfuTime: '1,5 ore in traghetto', paxosDesc: 'Grotte marine, acque turchesi e villaggi incantevoli.',
    paxosTime: '1 ora in barca', antipaxosDesc: 'Voutoumi e Vrika — due delle spiagge più belle della Grecia.',
    antipaxosTime: '1,5 ore in barca',
    thesprotiaRegion: 'Regione della Thesprotia', natureHiking: 'Natura & Escursioni',
    natureHikingDesc1: 'Esplora uliveti, sentieri antichi e colline verdi.',
    natureHikingDesc2: 'Dai villaggi di montagna ai sentieri costieri.',
    yourGateway: 'La Tua Porta', startIonianAdventure: 'Inizia la Tua Avventura Ionica',
    ionianAdventureDesc: 'Da Sivota, esplora Corfù, Paxos e Antipaxos.',
    heraHeroDesc: 'La vista più bella di Sivota — blu infinito e tramonti su Corfù',
    masterBedroom: 'Camera Principale',
    heraMasterTitle: 'Camera da Sogno con Tramonti Impareggiabili',
    heraMasterDesc: 'Questa splendida camera panoramica offre viste illimitate sul Mar Ionio.',
    secondaryBedroom: 'Camera Secondaria',
    heraSecondaryTitle: 'Camera Accogliente con Vista Mare',
    heraSecondaryDesc: 'Questa deliziosa camera secondaria ospita comodamente fino a tre persone.',
    kitchenDining: 'Cucina & Sala da Pranzo',
    heraKitchenTitle: 'Cucina di Lusso con Design Greco Tradizionale',
    heraKitchenDesc: 'Questa cucina lussuosa presenta un design in pietra e legno.',
    livingRoom: 'Soggiorno',
    heraLivingTitle: 'Soggiorno Regale con Eleganza Tradizionale',
    heraLivingDesc: 'Questo grandioso soggiorno con mobili tradizionali scolpiti.',
    secureYourDates: 'Prenota le date per il nostro rifugio in collina.',
    exceptionalOnBooking: 'Eccezionale · Booking.com',
    heraOverviewDesc1: 'L\'appartamento Hera incarna grazia, calore e bellezza senza tempo.',
    heraOverviewDesc2: 'Svegliati con il suono delle onde, goditi il caffè sulla terrazza privata.',
    zeusHeroDesc: 'Piscina privata, viste panoramiche e artigianato in pietra tradizionale',
    zeusOverviewDesc1: 'L\'appartamento Zeus cattura l\'attenzione con i suoi interni spaziosi.',
    zeusOverviewDesc2: 'Scopri il cortile incantevole con la piscina e gli eleganti lettini.',
    courtyardPool: 'Cortile & Piscina',
    zeusCourtTitle: 'Cortile Idilliaco con Piscina e Viste Panoramiche',
    zeusCourtDesc: 'Scopri il cortile incantevole — un\'oasi verde con piscina e bouganvillea.',
    zeusMasterTitle: 'Camera Principale con Arredi in Legno Tradizionali',
    zeusMasterDesc: 'Entra nell\'incantevole camera con mobili in legno fatti a mano.',
    twinBedroom: 'Camera Doppia',
    zeusTwinTitle: 'Camera Doppia Accogliente con Balcone & Vista Mare',
    zeusTwinDesc: 'Con due letti singoli, questa camera garantisce un soggiorno riposante.',
    stoneVerandaLabel: 'Veranda in Pietra',
    zeusVerandaTitle: 'Veranda in Pietra con Vista Panoramica Ionica',
    zeusVerandaDesc: 'La squisita veranda in pietra scolpita a mano con tetto in legno.',
    secureYourDatesZeus: 'Prenota le date per piscina e viste panoramiche.',
    seaViewBedrooms: 'Camere con vista mare', premiumBedLinens: 'Biancheria premium',
    fullyEquippedKitchen: 'Cucina completa', privateTerraceAmenity: 'Terrazza privata',
    airConditioningAmenity: 'Aria condizionata', highSpeedWiFiAmenity: 'Wi-Fi veloce',
    smartTVAmenity: 'Smart TV', washingMachineAmenity: 'Lavatrice',
    hairDryer: 'Asciugacapelli', ironIroningBoard: 'Ferro e asse da stiro',
    toiletries: 'Articoli da bagno', beachTowels: 'Teli mare',
    stoneBuiltDesign: 'Design in pietra', woodenDetails: 'Dettagli in legno',
    traditionalArchitecture: 'Architettura tradizionale', modernComforts: 'Comfort moderni',
    privatePoolAmenity: 'Piscina privata', panoramicSeaViewsAmenity: 'Viste panoramiche',
    spaciousCourtyard: 'Cortile spazioso',
    stoneBuiltVeranda: 'Veranda in pietra', woodenFurnishings: 'Arredi in legno',
    bougainvilleaPergola: 'Pergola di bouganvillea', sunLoungers: 'Lettini',
    weValuePrivacy: 'Rispettiamo la Tua Privacy',
    cookieDesc: 'Usiamo cookie essenziali per il funzionamento del sito. Nessun cookie di tracciamento.',
    decline: 'Rifiuta', accept: 'Accetta',
  },
  de: {
    home: 'Startseite', about: 'Über Uns', apartments: 'Apartments', destinations: 'Reiseziele',
    reviews: 'Bewertungen', booking: 'Buchung', contact: 'Kontakt',
    heroTitle: 'Sivota Royal', heroSubtitle: 'Luxus-Refugium',
    heroDesc: 'Zwei exklusive Residenzen für unvergessliche mediterrane Auszeiten.',
    welcome: 'Willkommen', welcomeTitle: 'Wo Luxus auf das Ionische Meer Trifft',
    welcomeDesc: 'An der unberührten Küste von Sivota gelegen, bieten unsere beiden exklusiven Apartments — Hera und Zeus — ein unvergleichliches Erlebnis mediterranen Luxus.',
    explore: 'Entdecken', exploreHera: 'Hera Entdecken', exploreZeus: 'Zeus Entdecken',
    theApartments: 'Die Apartments',
    heraDesc: 'Benannt nach der Königin der Götter, verkörpert das Hera Apartment Anmut und Eleganz.',
    zeusDesc: 'Beeindruckend und raffiniert, fängt das Zeus Apartment den Geist des Götterkönigs ein.',
    guestExperiences: 'Gästeerlebnisse', whatGuestsSay: 'Was Unsere Gäste Sagen',
    theExperience: 'Das Erlebnis', whySivotaRoyal: 'Warum Sivota Royal',
    privateLuxury: 'Privater Luxus', stunningViews: 'Atemberaubende Aussicht',
    nearBeaches: 'Nahe am Strand', mediterraneanLife: 'Mediterranes Leben',
    discover: 'Entdecken', sivotaBeyond: 'Sivota & Umgebung',
    sivotaBeyondDesc: 'Entdecken Sie versteckte Strände, charmante Häfen und authentische griechische Tavernen.',
    exploreDestinations: 'Reiseziele Entdecken',
    location: 'Standort', findUs: 'Finden Sie Uns',
    planEscape: 'Planen Sie Ihre Auszeit in Sivota',
    planEscapeDesc: 'Erleben Sie entspanntes mediterranes Leben im Sivota Royal.',
    checkAvailability: 'Verfügbarkeit Prüfen', sendEnquiry: 'Anfrage Senden',
    quickLinks: 'Schnelllinks', bookNow: 'Jetzt Buchen',
    footerDesc: 'Luxusapartments im Herzen von Sivota, Griechenland.',
    allRights: 'Alle Rechte vorbehalten.',
    overview: 'Überblick', amenities: 'Ausstattung', gallery: 'Galerie',
    graceElegance: 'Anmut & Eleganz', powerRefinement: 'Kraft & Raffinesse',
    everythingYouNeed: 'Alles was Sie Brauchen',
    readyExperience: 'Bereit zu Erleben',
    ourStory: 'Unsere Geschichte', philosophy: 'Philosophie',
    craftedTitle: 'Geschaffen für Jene, die Mehr Suchen',
    medLiving: 'Mediterranes Wohnen', beautyOfSivota: 'Die Schönheit von Sivota',
    theResidences: 'Die Residenzen', ourApartments: 'Unsere Apartments',
    testimonials: 'Erfahrungsberichte', guestReviews: 'Gästebewertungen',
    basedOnReviews: 'Basierend auf Gästebewertungen',
    reserve: 'Reservieren', bookYourStay: 'Buchen Sie Ihren Aufenthalt',
    option1: 'Option 1', option2: 'Option 2',
    sendAnEnquiry: 'Anfrage Senden', bookDirectly: 'Direkt Buchen',
    fullName: 'Vollständiger Name', email: 'E-Mail-Adresse', phone: 'Telefonnummer',
    checkin: 'Check-in', checkout: 'Check-out', guests: 'Anzahl der Gäste',
    message: 'Nachricht', sendMessage: 'Nachricht Senden',
    bookDirectlyDesc: 'Für sofortige Buchungen besuchen Sie unsere Booking.com-Seite.',
    getInTouch: 'Kontaktieren Sie Uns', contactDetails: 'Kontaktdaten',
    sivotaGreece: 'Sivota, Thesprotia, Griechenland',
    destinationsIntro: 'Sivota und seine Umgebung bieten eine Fülle von Erlebnissen.',
    whatsappMessage: 'Hallo! Ich interessiere mich für eine Buchung im Sivota Royal Luxury Retreat.',

    sivotaIonianGreece: 'Sivota · Ionische Küste · Griechenland',
    whereSeaMeetsSky: 'Wo das Meer den Himmel Trifft',
    heroLongDesc: 'Zwei luxuriöse Steinapartments über dem Ionischen Meer — aufwachen mit Korfu, einschlafen mit Sonnenuntergängen',
    viewApartments: 'Apartments Ansehen',
    ratingLabel: 'Bewertung 9.2', exceptionalBooking: 'Hervorragend auf Booking.com',
    onBookingCom: '9.2 auf Booking.com',
    hilltopViews: '🏔️ Panoramablick', twoMinBeach: '🌊 2 Min zum Strand', privatePool: '🏊 Privatpool',
    instantConfirmation: 'Sofortige Bestätigung', bookWithConfidence: 'Sicher buchen',
    freeCancellation: 'Kostenlose Stornierung', flexibleBooking: 'Flexible Buchungsoptionen',
    whereMedComesAlive: 'Wo das Mittelmeer Zum Leben Erwacht',
    indexIntroDesc: 'Auf einem malerischen Hügel im charmanten Dorf Sivota bieten unsere Steinapartments Panoramablick auf das Ionische Meer und Korfu.',
    heraPanoramicDesc: 'Ein Panorama-Zufluchtsort mit grenzenlosem Blick auf das Ionische Meer und Korfu.',
    zeusPoolDesc: 'Ein luxuriöses Refugium mit privatem Pool und Panoramablick auf das Ionische Meer.',
    seaViewsTerrace: 'Meerblick & Terrasse', privatePoolCourtyard: 'Privatpool & Innenhof',
    twoBeds: '2 Betten', seaViews: 'Meerblick', wiFi: 'Wi-Fi', ac: 'Klimaanlage',
    kitchen: 'Küche', terrace: 'Terrasse', pool: 'Pool', views: 'Aussicht',
    twoBedrooms: '2 Schlafzimmer', freeWiFi: 'Kostenloses Wi-Fi', fullKitchen: 'Voll ausgestattete Küche',
    privatePoolLabel: 'Privatpool', panoramicViews: 'Panoramablick',
    seaCorfuViews: 'Meer- & Korfu-Blick', privateTerrace: 'Private Terrasse',
    airConditioning: 'Klimaanlage', highSpeedWiFi: 'Highspeed-Wi-Fi',
    smartTV: 'Smart TV', washingMachine: 'Waschmaschine',
    stoneVeranda: 'Steinveranda', panoramicSeaViews: 'Panorama-Meerblick',
    bestViewsQuote: '"Die besten Aussichten von ganz Sivota"', ourGuests: '— Unsere Gäste',
    medLivingFinest: 'Mediterranes Leben vom Feinsten',
    sunDrenchedTerraces: 'Sonnige Terrassen', sunDrenchedDesc: 'Frühstück in der Sonne, Abendessen unter Sternen',
    endlessBlueViews: 'Endloses Blau', endlessBlueDesc: 'Panoramablick auf das Ionische Meer und Korfu',
    crystalClearBeaches: 'Kristallklare Strände', crystalClearDesc: 'Versteckte Buchten und türkisfarbenes Wasser',
    authenticGreekLife: 'Authentisches Griechisches Leben', authenticGreekDesc: 'Tavernen, Häfen und Insel-Abenteuer',
    sunSeaStone: 'Sonne, Meer & Stein',
    islandHoppingFromSivota: 'Inselhopping ab Sivota',
    islandHoppingDesc: 'Entdecken Sie die Juwelen des Ionischen Meeres',
    readAllReviews: 'Alle Bewertungen lesen',
    startPlanning: 'Jetzt Planen',
    yourMedEscape: 'Ihre Mediterrane Auszeit Erwartet Sie',
    bookDirectlyBestRates: 'Buchen Sie direkt für die besten Preise und einen persönlichen Empfang von Dimitris',
    yourStoryStartsHere: 'Ihre Geschichte Beginnt Hier',
    readyExperienceSivotaRoyal: 'Bereit für Sivota Royal?',
    readyExperienceSivota: 'Bereit für Sivota?',
    bookDirectlyBestRatesDimitris: 'Direkt buchen für die besten Preise',
    builtWithLove: 'Mit Liebe Gebaut, in Sonnenlicht Getaucht',
    notJustPlace: 'Dies ist Nicht Nur ein Ort zum Übernachten',
    aboutIntro1: 'Stellen Sie sich vor, Sie wachen mit dem Klang des Meeres auf und treten auf eine sonnige Terrasse.',
    aboutIntro2: 'Sivota Royal wurde nicht im Büro entworfen — es wurde von Hand gebaut, Stein für Stein.',
    whatSetsUsApart: 'Was uns Unterscheidet', moreThanAccommodation: 'Mehr als eine Unterkunft',
    handcrafted: 'Handgefertigt', handcraftedDesc: 'Jeder Stein von Hand gesetzt, jedes Detail bedacht.',
    bestViews: 'Beste Aussicht', bestViewsDesc: 'Terrassenblick auf das Ionische Meer und Korfu.',
    twoMinToBeach: '2 Min zum Strand', twoMinToBeachDesc: 'Kristallklares Wasser in wenigen Schritten.',
    authentic: 'Authentisch', authenticDesc: 'Kein Trubel. Der Rhythmus des griechischen Küstenlebens.',
    theSetting: 'Die Umgebung', everyMorningGift: 'Jeder Morgen Fühlt sich wie ein Geschenk An',
    settingDesc1: 'Goldenes Sonnenlicht strömt durch Ihre Fenster.',
    settingDesc2: 'Das ist kein Traum — das ist Ihr Morgen im Sivota Royal.',
    greeceBestKeptSecret: 'Griechenlands Bestgehütetes Geheimnis',
    locationDesc1: 'Sivota ist ein verstecktes Juwel an der Küste von Thesprotien.',
    locationDesc2: 'Entdecken Sie versteckte Buchten, dinieren Sie am Hafen.',
    quoteNotHotel: '"Wir haben kein Hotel gebaut. Wir haben den Ort gebaut, an dem wir selbst bleiben möchten."',
    ownerName: '— Dimitris Vlachos, Eigentümer',
    theProperty: 'Das Anwesen', everyDetailConsidered: 'Jedes Detail, Durchdacht',
    personalWelcome: 'Ein Persönlicher Empfang', meetDimitris: 'Lernen Sie Dimitris Kennen',
    dimitrisQuote1: '"Ich bin Dimitris, und Sivota Royal ist mein Lebenswerk."',
    dimitrisQuote2: '"Wenn Sie ankommen, werde ich Sie persönlich begrüßen."',
    yourHost: 'Ihr Gastgeber', dimitrisMadeFeel: '"Dimitris hat uns das Gefühl gegeben, zur Familie zu gehören"',
    twoExceptionalResidences: 'Zwei außergewöhnliche Residenzen mit eigenem Charakter.',
    heraExtendedDesc: 'Dieses atemberaubende Panorama-Apartment bietet grenzenlose Meerblicke.',
    zeusExtendedDesc: 'Ein luxuriöses Apartment mit privatem Pool und Panoramablick.',
    stayDetails: 'Aufenthaltsdetails', yourDetails: 'Ihre Daten',
    selectYourDates: 'Wählen Sie Ihre Daten', total: 'Gesamt', discount: 'Rabatt',
    couponCode: 'Gutscheincode', enterCode: 'Code eingeben', apply: 'Anwenden',
    continueBtn: 'Weiter', back: 'Zurück', sending: 'Wird gesendet...',
    yourSelection: 'Ihre Auswahl', editSelection: 'Auswahl bearbeiten',
    apartment: 'Apartment',
    chatWithUs: 'Direkt Chatten', chatWithUsDesc: 'Lieber schreiben? Kontaktieren Sie uns auf WhatsApp.',
    chatOnWhatsApp: '💬 WhatsApp Chat',
    noPricingSet: 'Kein Preis festgelegt — wir bestätigen den Tarif.',
    minimumStay: 'Mindestaufenthalt {min} Nächte',
    thankYouEnquiry: 'Vielen Dank! Ihre Anfrage wurde gesendet.',
    fillNameEmail: 'Bitte Name und E-Mail ausfüllen.', selectApartment: 'Bitte Apartment wählen.',
    selectDates: 'Bitte Daten auswählen.', invalidCoupon: 'Ungültiger Gutscheincode',
    viewOnBooking: 'Auf Booking.com ansehen',
    fillRequiredFields: 'Bitte alle Pflichtfelder ausfüllen.',
    messageSent: 'Nachricht gesendet! Wir melden uns bald.',
    joinOurGuests: 'Werden Sie Unser Gast', createYourStory: 'Schreiben Sie Ihre Geschichte',
    featuredQuote: '"Die besten Aussichten von ganz Sivota — ein unvergessliches Erlebnis"',
    featuredQuoteAuthor: '— Maria K., Athen',
    hiddenCovesDesc: 'Versteckte Buchten, Insel-Abenteuer und authentische griechische Erlebnisse',
    secludedBeaches: 'Abgelegene Strände', secludedBeachesDesc: 'Kristallklares Wasser und weiße Kieselstrände.',
    sivotaCoastline: 'Küste von Sivota', boatTrips: 'Bootsausflüge',
    boatTripsDesc: 'Entdecken Sie die Ionischen Inseln und versteckte Meereshöhlen.', ionianSea: 'Ionisches Meer',
    sivotaHarbour: 'Hafen von Sivota', sivotaHarbourDesc: 'Flanieren Sie am Hafen und genießen Sie frischen Fisch.',
    sivotaVillage: 'Dorf Sivota', localDining: 'Lokale Küche',
    localDiningDesc: 'Von Tavernen am Meer bis zu Bergrestaurants.', sivotaSurrounds: 'Sivota & Umgebung',
    islandHopping: 'Inselhopping', jewelsOfIonian: 'Juwelen des Ionischen Meeres',
    islandHoppingIntro: 'Von Sivota aus drei der schönsten Inseln des Mittelmeers entdecken',
    corfuDesc: 'UNESCO-Altstadt, Achilleion-Palast und traumhafte Strände.',
    corfuTime: '1,5 Std. per Fähre', paxosDesc: 'Meereshöhlen, türkises Wasser und charmante Dörfer.',
    paxosTime: '1 Std. per Boot', antipaxosDesc: 'Voutoumi und Vrika — zwei der schönsten Strände Griechenlands.',
    antipaxosTime: '1,5 Std. per Boot',
    thesprotiaRegion: 'Region Thesprotien', natureHiking: 'Natur & Wandern',
    natureHikingDesc1: 'Olivenhaine, antike Pfade und grüne Hügel erkunden.',
    natureHikingDesc2: 'Von Bergdörfern bis zu Küstenwegen.',
    yourGateway: 'Ihr Tor', startIonianAdventure: 'Starten Sie Ihr Ionisches Abenteuer',
    ionianAdventureDesc: 'Von Sivota aus Korfu, Paxos und Antipaxos entdecken.',
    heraHeroDesc: 'Die schönste Aussicht von Sivota — endloses Blau und Korfu-Sonnenuntergänge',
    masterBedroom: 'Hauptschlafzimmer',
    heraMasterTitle: 'Traumhaftes Schlafzimmer mit Einzigartigen Sonnenuntergängen',
    heraMasterDesc: 'Dieses atemberaubende Panorama-Schlafzimmer bietet grenzenlose Aussicht.',
    secondaryBedroom: 'Zweites Schlafzimmer',
    heraSecondaryTitle: 'Charmantes Schlafzimmer mit Meerblick',
    heraSecondaryDesc: 'Dieses reizende zweite Schlafzimmer bietet Platz für bis zu drei Gäste.',
    kitchenDining: 'Küche & Essbereich',
    heraKitchenTitle: 'Luxuriöse Küche mit Griechischem Traditionalem Design',
    heraKitchenDesc: 'Diese luxuriöse Küche mit Stein- und Holzdesign erinnert an traditionelle griechische Häuser.',
    livingRoom: 'Wohnzimmer',
    heraLivingTitle: 'Majestätisches Wohnzimmer mit Traditioneller Eleganz',
    heraLivingDesc: 'Dieses großartige Wohnzimmer strahlt mit geschnitzten Möbeln königliches Flair aus.',
    secureYourDates: 'Sichern Sie sich Ihre Daten in unserem Hügel-Refugium.',
    exceptionalOnBooking: 'Hervorragend · Booking.com',
    heraOverviewDesc1: 'Das Hera Apartment verkörpert Anmut, Wärme und zeitlose Schönheit.',
    heraOverviewDesc2: 'Wachen Sie mit dem Wellenrauschen auf und genießen Sie Kaffee auf der Terrasse.',
    zeusHeroDesc: 'Privatpool, Panoramablick und traditionelle Steinbauweise',
    zeusOverviewDesc1: 'Das Zeus Apartment beeindruckt mit geräumigen Innenräumen und Natursteinakzenten.',
    zeusOverviewDesc2: 'Entdecken Sie den bezaubernden Innenhof mit Pool und eleganten Sonnenliegen.',
    courtyardPool: 'Innenhof & Pool',
    zeusCourtTitle: 'Idyllischer Innenhof mit Pool und Panoramablick',
    zeusCourtDesc: 'Entdecken Sie den bezaubernden Innenhof — eine grüne Oase mit Pool und Bougainvillea.',
    zeusMasterTitle: 'Charmantes Hauptschlafzimmer mit Holzmöbeln',
    zeusMasterDesc: 'Treten Sie ein in das bezaubernde Schlafzimmer mit handgefertigten Holzmöbeln.',
    twinBedroom: 'Zweibettzimmer',
    zeusTwinTitle: 'Gemütliches Zweibettzimmer mit Balkon & Meerblick',
    zeusTwinDesc: 'Mit zwei Einzelbetten sorgt dieses Zimmer für erholsamen Schlaf.',
    stoneVerandaLabel: 'Steinveranda',
    zeusVerandaTitle: 'Traditionelle Steinveranda mit Ionischem Panoramablick',
    zeusVerandaDesc: 'Die exquisite handgeschnitzte Steinveranda mit Holzdach.',
    secureYourDatesZeus: 'Sichern Sie sich Pool und Panoramablick.',
    seaViewBedrooms: 'Schlafzimmer mit Meerblick', premiumBedLinens: 'Premium-Bettwäsche',
    fullyEquippedKitchen: 'Voll ausgestattete Küche', privateTerraceAmenity: 'Private Terrasse',
    airConditioningAmenity: 'Klimaanlage', highSpeedWiFiAmenity: 'Highspeed-Wi-Fi',
    smartTVAmenity: 'Smart TV', washingMachineAmenity: 'Waschmaschine',
    hairDryer: 'Haartrockner', ironIroningBoard: 'Bügeleisen & Bügelbrett',
    toiletries: 'Pflegeprodukte', beachTowels: 'Strandtücher',
    stoneBuiltDesign: 'Steinbau-Design', woodenDetails: 'Holzdetails',
    traditionalArchitecture: 'Traditionelle Architektur', modernComforts: 'Moderner Komfort',
    privatePoolAmenity: 'Privatpool', panoramicSeaViewsAmenity: 'Panorama-Meerblick',
    spaciousCourtyard: 'Geräumiger Innenhof',
    stoneBuiltVeranda: 'Steinveranda', woodenFurnishings: 'Holzmöbel',
    bougainvilleaPergola: 'Bougainvillea-Pergola', sunLoungers: 'Sonnenliegen',
    weValuePrivacy: 'Wir Schätzen Ihre Privatsphäre',
    cookieDesc: 'Wir verwenden nur essenzielle Cookies für die Funktion der Website. Keine Tracking-Cookies.',
    decline: 'Ablehnen', accept: 'Akzeptieren',
  },
  fr: {
    home: 'Accueil', about: 'À Propos', apartments: 'Appartements', destinations: 'Destinations',
    reviews: 'Avis', booking: 'Réservation', contact: 'Contact',
    heroTitle: 'Sivota Royal', heroSubtitle: 'Retraite de Luxe',
    heroDesc: 'Deux résidences exclusives conçues pour des escapades méditerranéennes inoubliables.',
    welcome: 'Bienvenue', welcomeTitle: 'Où le Luxe Rencontre la Mer Ionienne',
    welcomeDesc: 'Nichés sur la côte préservée de Sivota, nos deux appartements exclusifs — Hera et Zeus — offrent une expérience inégalée de luxe méditerranéen.',
    explore: 'Explorer', exploreHera: 'Explorer Hera', exploreZeus: 'Explorer Zeus',
    theApartments: 'Les Appartements',
    heraDesc: 'Nommé d\'après la reine des dieux, l\'appartement Hera incarne la grâce et l\'élégance.',
    zeusDesc: 'Imposant et raffiné, l\'appartement Zeus capture l\'esprit du roi de l\'Olympe.',
    guestExperiences: 'Expériences des Clients', whatGuestsSay: 'Ce que Disent Nos Clients',
    theExperience: 'L\'Expérience', whySivotaRoyal: 'Pourquoi Sivota Royal',
    privateLuxury: 'Luxe Privé', stunningViews: 'Vues Spectaculaires',
    nearBeaches: 'Proche des Plages', mediterraneanLife: 'Vie Méditerranéenne',
    discover: 'Découvrir', sivotaBeyond: 'Sivota et Au-delà',
    sivotaBeyondDesc: 'Explorez des plages cachées, des ports charmants et d\'authentiques tavernes grecques.',
    exploreDestinations: 'Explorer les Destinations',
    location: 'Emplacement', findUs: 'Nous Trouver',
    planEscape: 'Planifiez Votre Évasion à Sivota',
    planEscapeDesc: 'Vivez la vie méditerranéenne détendue au Sivota Royal.',
    checkAvailability: 'Vérifier la Disponibilité', sendEnquiry: 'Envoyer une Demande',
    quickLinks: 'Liens Rapides', bookNow: 'Réserver',
    footerDesc: 'Appartements de luxe au cœur de Sivota, Grèce.',
    allRights: 'Tous droits réservés.',
    overview: 'Aperçu', amenities: 'Équipements', gallery: 'Galerie',
    graceElegance: 'Grâce & Élégance', powerRefinement: 'Puissance & Raffinement',
    everythingYouNeed: 'Tout ce Dont Vous Avez Besoin',
    readyExperience: 'Prêt à Vivre',
    ourStory: 'Notre Histoire', philosophy: 'Philosophie',
    craftedTitle: 'Conçu pour Ceux qui Cherchent Plus',
    medLiving: 'Vie Méditerranéenne', beautyOfSivota: 'La Beauté de Sivota',
    theResidences: 'Les Résidences', ourApartments: 'Nos Appartements',
    testimonials: 'Témoignages', guestReviews: 'Avis des Clients',
    basedOnReviews: 'Basé sur les avis des clients',
    reserve: 'Réserver', bookYourStay: 'Réservez Votre Séjour',
    option1: 'Option 1', option2: 'Option 2',
    sendAnEnquiry: 'Envoyer une Demande', bookDirectly: 'Réserver Directement',
    fullName: 'Nom Complet', email: 'Adresse E-mail', phone: 'Numéro de Téléphone',
    checkin: 'Arrivée', checkout: 'Départ', guests: 'Nombre d\'Invités',
    message: 'Message', sendMessage: 'Envoyer le Message',
    bookDirectlyDesc: 'Pour une réservation instantanée, visitez notre page Booking.com.',
    getInTouch: 'Contactez-Nous', contactDetails: 'Coordonnées',
    sivotaGreece: 'Sivota, Thesprotie, Grèce',
    destinationsIntro: 'Sivota et ses environs offrent une multitude d\'expériences.',
    whatsappMessage: 'Bonjour! Je suis intéressé par une réservation au Sivota Royal Luxury Retreat.',

    sivotaIonianGreece: 'Sivota · Côte Ionienne · Grèce',
    whereSeaMeetsSky: 'Où la Mer Rencontre le Ciel',
    heroLongDesc: 'Deux appartements de luxe en pierre surplombant la Mer Ionienne — réveillez-vous face à Corfou, endormez-vous avec les couchers de soleil',
    viewApartments: 'Voir les Appartements',
    ratingLabel: 'Note 9.2', exceptionalBooking: 'Exceptionnel sur Booking.com',
    onBookingCom: '9.2 sur Booking.com',
    hilltopViews: '🏔️ Vue Panoramique', twoMinBeach: '🌊 2 min de la Plage', privatePool: '🏊 Piscine Privée',
    instantConfirmation: 'Confirmation Immédiate', bookWithConfidence: 'Réservez en toute confiance',
    freeCancellation: 'Annulation Gratuite', flexibleBooking: 'Options de réservation flexibles',
    whereMedComesAlive: 'Où la Méditerranée Prend Vie',
    indexIntroDesc: 'Au sommet d\'une colline pittoresque dans le village de Sivota, nos appartements en pierre offrent des vues panoramiques sur la Mer Ionienne et Corfou.',
    heraPanoramicDesc: 'Un sanctuaire panoramique avec des vues illimitées sur la Mer Ionienne et Corfou.',
    zeusPoolDesc: 'Une retraite de luxe avec piscine privée et vues panoramiques sur l\'Ionien.',
    seaViewsTerrace: 'Vue mer & terrasse', privatePoolCourtyard: 'Piscine privée & cour',
    twoBeds: '2 Lits', seaViews: 'Vue Mer', wiFi: 'Wi-Fi', ac: 'Clim',
    kitchen: 'Cuisine', terrace: 'Terrasse', pool: 'Piscine', views: 'Vues',
    twoBedrooms: '2 Chambres', freeWiFi: 'Wi-Fi Gratuit', fullKitchen: 'Cuisine Équipée',
    privatePoolLabel: 'Piscine Privée', panoramicViews: 'Vues Panoramiques',
    seaCorfuViews: 'Vue Mer & Corfou', privateTerrace: 'Terrasse Privée',
    airConditioning: 'Climatisation', highSpeedWiFi: 'Wi-Fi Haut Débit',
    smartTV: 'Smart TV', washingMachine: 'Machine à Laver',
    stoneVeranda: 'Véranda en Pierre', panoramicSeaViews: 'Vues Panoramiques sur la Mer',
    bestViewsQuote: '"Les plus belles vues de tout Sivota"', ourGuests: '— Nos Clients',
    medLivingFinest: 'La Vie Méditerranéenne à son Meilleur',
    sunDrenchedTerraces: 'Terrasses Ensoleillées', sunDrenchedDesc: 'Petit-déjeuner au soleil, dîner sous les étoiles',
    endlessBlueViews: 'Bleu Infini', endlessBlueDesc: 'Panorama sur la Mer Ionienne et Corfou',
    crystalClearBeaches: 'Plages Cristallines', crystalClearDesc: 'Criques cachées et eaux turquoise',
    authenticGreekLife: 'Vie Grecque Authentique', authenticGreekDesc: 'Tavernes, ports et aventures insulaires',
    sunSeaStone: 'Soleil, Mer & Pierre',
    islandHoppingFromSivota: 'Island Hopping depuis Sivota',
    islandHoppingDesc: 'Explorez les joyaux de la Mer Ionienne — tous à portée de main',
    readAllReviews: 'Lire tous les avis',
    startPlanning: 'Commencer à Planifier',
    yourMedEscape: 'Votre Évasion Méditerranéenne Vous Attend',
    bookDirectlyBestRates: 'Réservez directement pour les meilleurs tarifs et un accueil personnel de Dimitris',
    yourStoryStartsHere: 'Votre Histoire Commence Ici',
    readyExperienceSivotaRoyal: 'Prêt à Vivre Sivota Royal ?',
    readyExperienceSivota: 'Prêt à Vivre Sivota ?',
    bookDirectlyBestRatesDimitris: 'Réservez directement pour les meilleurs tarifs',
    builtWithLove: 'Construit avec Amour, Baigné de Lumière',
    notJustPlace: 'Ce n\'est Pas Juste un Lieu de Séjour',
    aboutIntro1: 'Imaginez vous réveiller au son de la mer, sortir sur une terrasse ensoleillée.',
    aboutIntro2: 'Sivota Royal n\'a pas été conçu dans un bureau — il a été construit à la main, pierre par pierre.',
    whatSetsUsApart: 'Ce Qui Nous Distingue', moreThanAccommodation: 'Plus qu\'un Hébergement',
    handcrafted: 'Artisanal', handcraftedDesc: 'Chaque pierre posée à la main, chaque détail soigné.',
    bestViews: 'Meilleures Vues', bestViewsDesc: 'Terrasses panoramiques sur la Mer Ionienne et Corfou.',
    twoMinToBeach: '2 Min de la Plage', twoMinToBeachDesc: 'Eaux cristallines à quelques pas.',
    authentic: 'Authentique', authenticDesc: 'Pas de foule. Le rythme de la vie côtière grecque.',
    theSetting: 'Le Cadre', everyMorningGift: 'Chaque Matin Est un Cadeau',
    settingDesc1: 'La lumière dorée entre par vos fenêtres. Vous sortez sur votre terrasse privée.',
    settingDesc2: 'Ce n\'est pas un rêve — c\'est votre matin au Sivota Royal.',
    greeceBestKeptSecret: 'Le Secret le Mieux Gardé de Grèce',
    locationDesc1: 'Sivota est un joyau caché sur la côte de Thesprotie.',
    locationDesc2: 'Explorez des criques cachées, dînez au port, admirez le coucher de soleil.',
    quoteNotHotel: '"Nous n\'avons pas construit un hôtel. Nous avons construit l\'endroit où nous voudrions séjourner."',
    ownerName: '— Dimitris Vlachos, Propriétaire',
    theProperty: 'La Propriété', everyDetailConsidered: 'Chaque Détail, Pensé',
    personalWelcome: 'Un Accueil Personnel', meetDimitris: 'Rencontrez Dimitris',
    dimitrisQuote1: '"Je suis Dimitris, et Sivota Royal est l\'œuvre de ma vie."',
    dimitrisQuote2: '"Quand vous arriverez, je serai là pour vous accueillir personnellement."',
    yourHost: 'Votre Hôte', dimitrisMadeFeel: '"Dimitris nous a fait sentir comme en famille"',
    twoExceptionalResidences: 'Deux résidences exceptionnelles, chacune avec son propre caractère.',
    heraExtendedDesc: 'Cet appartement panoramique offre des vues illimitées sur la Mer Ionienne.',
    zeusExtendedDesc: 'Un appartement de luxe avec piscine privée et vues panoramiques.',
    stayDetails: 'Détails du Séjour', yourDetails: 'Vos Coordonnées',
    selectYourDates: 'Sélectionnez vos dates', total: 'Total', discount: 'Réduction',
    couponCode: 'Code Coupon', enterCode: 'Entrez le code', apply: 'Appliquer',
    continueBtn: 'Continuer', back: 'Retour', sending: 'Envoi...',
    yourSelection: 'Votre Sélection', editSelection: 'Modifier la sélection',
    apartment: 'Appartement',
    chatWithUs: 'Discutez Directement', chatWithUsDesc: 'Préférez écrire ? Contactez-nous sur WhatsApp.',
    chatOnWhatsApp: '💬 Discuter sur WhatsApp',
    noPricingSet: 'Pas de tarif défini — nous confirmerons.',
    minimumStay: 'Séjour minimum {min} nuits',
    thankYouEnquiry: 'Merci ! Votre demande a été envoyée.',
    fillNameEmail: 'Veuillez remplir nom et email.', selectApartment: 'Veuillez sélectionner un appartement.',
    selectDates: 'Veuillez sélectionner les dates.', invalidCoupon: 'Code coupon invalide',
    viewOnBooking: 'Voir sur Booking.com',
    fillRequiredFields: 'Veuillez remplir tous les champs obligatoires.',
    messageSent: 'Message envoyé ! Nous reviendrons vers vous bientôt.',
    joinOurGuests: 'Rejoignez Nos Clients', createYourStory: 'Créez Votre Histoire',
    featuredQuote: '"Les plus belles vues de tout Sivota — une expérience inoubliable"',
    featuredQuoteAuthor: '— Maria K., Athènes',
    hiddenCovesDesc: 'Criques cachées, aventures insulaires et expériences grecques authentiques',
    secludedBeaches: 'Plages Isolées', secludedBeachesDesc: 'Eaux cristallines et galets blancs le long de la côte.',
    sivotaCoastline: 'Côte de Sivota', boatTrips: 'Excursions en Bateau',
    boatTripsDesc: 'Explorez les îles Ioniennes et les grottes marines cachées.', ionianSea: 'Mer Ionienne',
    sivotaHarbour: 'Port de Sivota', sivotaHarbourDesc: 'Promenez-vous le long du port et dégustez du poisson frais.',
    sivotaVillage: 'Village de Sivota', localDining: 'Gastronomie Locale',
    localDiningDesc: 'Des tavernes en bord de mer aux restaurants sur les collines.', sivotaSurrounds: 'Sivota et Environs',
    islandHopping: 'Island Hopping', jewelsOfIonian: 'Joyaux de l\'Ionien',
    islandHoppingIntro: 'Depuis Sivota, explorez trois des plus belles îles de la Méditerranée',
    corfuDesc: 'Vieille ville UNESCO, Palais Achilleion et plages comme Paleokastritsa.',
    corfuTime: '1h30 en ferry', paxosDesc: 'Grottes marines, eaux turquoise et villages charmants.',
    paxosTime: '1h en bateau', antipaxosDesc: 'Voutoumi et Vrika — deux des plus belles plages de Grèce.',
    antipaxosTime: '1h30 en bateau',
    thesprotiaRegion: 'Région de Thesprotie', natureHiking: 'Nature & Randonnée',
    natureHikingDesc1: 'Explorez oliveraies, sentiers anciens et collines verdoyantes.',
    natureHikingDesc2: 'Des villages de montagne aux sentiers côtiers.',
    yourGateway: 'Votre Porte d\'Entrée', startIonianAdventure: 'Commencez Votre Aventure Ionienne',
    ionianAdventureDesc: 'Depuis Sivota, explorez Corfou, Paxos et Antipaxos.',
    heraHeroDesc: 'La plus belle vue de Sivota — bleu ionien infini et couchers de soleil sur Corfou',
    masterBedroom: 'Chambre Principale',
    heraMasterTitle: 'Chambre de Rêve avec Couchers de Soleil Incomparables',
    heraMasterDesc: 'Cette chambre panoramique offre des vues illimitées sur la Mer Ionienne.',
    secondaryBedroom: 'Chambre Secondaire',
    heraSecondaryTitle: 'Chambre Charmante avec Vue sur la Mer',
    heraSecondaryDesc: 'Cette charmante chambre secondaire accueille confortablement jusqu\'à trois personnes.',
    kitchenDining: 'Cuisine & Salle à Manger',
    heraKitchenTitle: 'Cuisine de Luxe au Design Grec Traditionnel',
    heraKitchenDesc: 'Cette cuisine luxueuse présente un design en pierre et bois.',
    livingRoom: 'Salon',
    heraLivingTitle: 'Salon Royal avec Élégance Traditionnelle',
    heraLivingDesc: 'Ce grand salon avec mobilier traditionnel sculpté.',
    secureYourDates: 'Réservez vos dates dans notre sanctuaire sur la colline.',
    exceptionalOnBooking: 'Exceptionnel · Booking.com',
    heraOverviewDesc1: 'L\'appartement Hera incarne grâce, chaleur et beauté intemporelle.',
    heraOverviewDesc2: 'Réveillez-vous au son des vagues, savourez votre café sur la terrasse.',
    zeusHeroDesc: 'Piscine privée, vues panoramiques et artisanat en pierre traditionnel',
    zeusOverviewDesc1: 'L\'appartement Zeus impressionne par ses intérieurs spacieux.',
    zeusOverviewDesc2: 'Découvrez la cour enchantée avec piscine et chaises longues élégantes.',
    courtyardPool: 'Cour & Piscine',
    zeusCourtTitle: 'Cour Idyllique avec Piscine et Vue Panoramique',
    zeusCourtDesc: 'Découvrez la cour enchantée — une oasis verte avec piscine et bougainvilliers.',
    zeusMasterTitle: 'Chambre Principale avec Mobilier en Bois Traditionnel',
    zeusMasterDesc: 'Entrez dans la chambre enchantée avec du mobilier en bois fait main.',
    twinBedroom: 'Chambre Double',
    zeusTwinTitle: 'Chambre Double Confortable avec Balcon & Vue Mer',
    zeusTwinDesc: 'Avec deux lits simples, cette chambre assure un séjour reposant.',
    stoneVerandaLabel: 'Véranda en Pierre',
    zeusVerandaTitle: 'Véranda en Pierre avec Vue Panoramique Ionienne',
    zeusVerandaDesc: 'L\'exquise véranda en pierre sculptée à la main avec toit en bois.',
    secureYourDatesZeus: 'Réservez vos dates pour la piscine et les vues panoramiques.',
    seaViewBedrooms: 'Chambres vue mer', premiumBedLinens: 'Linge de lit premium',
    fullyEquippedKitchen: 'Cuisine équipée', privateTerraceAmenity: 'Terrasse privée',
    airConditioningAmenity: 'Climatisation', highSpeedWiFiAmenity: 'Wi-Fi haut débit',
    smartTVAmenity: 'Smart TV', washingMachineAmenity: 'Machine à laver',
    hairDryer: 'Sèche-cheveux', ironIroningBoard: 'Fer et planche à repasser',
    toiletries: 'Articles de toilette', beachTowels: 'Serviettes de plage',
    stoneBuiltDesign: 'Design en pierre', woodenDetails: 'Détails en bois',
    traditionalArchitecture: 'Architecture traditionnelle', modernComforts: 'Confort moderne',
    privatePoolAmenity: 'Piscine privée', panoramicSeaViewsAmenity: 'Vues panoramiques',
    spaciousCourtyard: 'Cour spacieuse',
    stoneBuiltVeranda: 'Véranda en pierre', woodenFurnishings: 'Mobilier en bois',
    bougainvilleaPergola: 'Pergola de bougainvilliers', sunLoungers: 'Chaises longues',
    weValuePrivacy: 'Nous Respectons Votre Vie Privée',
    cookieDesc: 'Nous utilisons des cookies essentiels uniquement. Aucun cookie de suivi.',
    decline: 'Refuser', accept: 'Accepter',
  },
  nl: {
    home: 'Home', about: 'Over Ons', apartments: 'Appartementen', destinations: 'Bestemmingen',
    reviews: 'Beoordelingen', booking: 'Boeken', contact: 'Contact',
    heroTitle: 'Sivota Royal', heroSubtitle: 'Luxe Toevluchtsoord',
    heroDesc: 'Twee exclusieve residenties ontworpen voor onvergetelijke mediterrane uitjes.',
    welcome: 'Welkom', welcomeTitle: 'Waar Luxe de Ionische Zee Ontmoet',
    welcomeDesc: 'Genesteld aan de ongerepte kust van Sivota bieden onze twee exclusieve appartementen — Hera en Zeus — een ongeëvenaarde ervaring van mediterrane luxe.',
    explore: 'Ontdek', exploreHera: 'Ontdek Hera', exploreZeus: 'Ontdek Zeus',
    theApartments: 'De Appartementen',
    heraDesc: 'Vernoemd naar de koningin der goden, belichaamt het Hera Appartement gratie en elegantie.',
    zeusDesc: 'Indrukwekkend en verfijnd, vangt het Zeus Appartement de geest van de koning van de Olympus.',
    guestExperiences: 'Gastervaringen', whatGuestsSay: 'Wat Onze Gasten Zeggen',
    theExperience: 'De Ervaring', whySivotaRoyal: 'Waarom Sivota Royal',
    privateLuxury: 'Privé Luxe', stunningViews: 'Adembenemend Uitzicht',
    nearBeaches: 'Dichtbij Stranden', mediterraneanLife: 'Mediterraan Leven',
    discover: 'Ontdek', sivotaBeyond: 'Sivota & Verder',
    sivotaBeyondDesc: 'Ontdek verborgen stranden, charmante havens en authentieke Griekse taverna\'s.',
    exploreDestinations: 'Ontdek Bestemmingen',
    location: 'Locatie', findUs: 'Vind Ons',
    planEscape: 'Plan Uw Uitje naar Sivota',
    planEscapeDesc: 'Beleef ontspannen mediterraan leven bij Sivota Royal.',
    checkAvailability: 'Beschikbaarheid Controleren', sendEnquiry: 'Aanvraag Versturen',
    quickLinks: 'Snelle Links', bookNow: 'Nu Boeken',
    footerDesc: 'Luxe appartementen in het hart van Sivota, Griekenland.',
    allRights: 'Alle rechten voorbehouden.',
    overview: 'Overzicht', amenities: 'Voorzieningen', gallery: 'Galerij',
    graceElegance: 'Gratie & Elegantie', powerRefinement: 'Kracht & Verfijning',
    everythingYouNeed: 'Alles Wat U Nodig Heeft',
    readyExperience: 'Klaar om te Ervaren',
    ourStory: 'Ons Verhaal', philosophy: 'Filosofie',
    craftedTitle: 'Gemaakt voor Wie Meer Zoekt',
    medLiving: 'Mediterraan Wonen', beautyOfSivota: 'De Schoonheid van Sivota',
    theResidences: 'De Residenties', ourApartments: 'Onze Appartementen',
    testimonials: 'Getuigenissen', guestReviews: 'Gastbeoordelingen',
    basedOnReviews: 'Gebaseerd op gastbeoordelingen',
    reserve: 'Reserveren', bookYourStay: 'Boek Uw Verblijf',
    option1: 'Optie 1', option2: 'Optie 2',
    sendAnEnquiry: 'Aanvraag Versturen', bookDirectly: 'Direct Boeken',
    fullName: 'Volledige Naam', email: 'E-mailadres', phone: 'Telefoonnummer',
    checkin: 'Inchecken', checkout: 'Uitchecken', guests: 'Aantal Gasten',
    message: 'Bericht', sendMessage: 'Bericht Versturen',
    bookDirectlyDesc: 'Voor directe boeking, bezoek onze Booking.com-pagina.',
    getInTouch: 'Neem Contact Op', contactDetails: 'Contactgegevens',
    sivotaGreece: 'Sivota, Thesprotia, Griekenland',
    destinationsIntro: 'Sivota en omgeving bieden een schat aan ervaringen.',
    whatsappMessage: 'Hallo! Ik ben geïnteresseerd in een boeking bij Sivota Royal Luxury Retreat.',

    sivotaIonianGreece: 'Sivota · Ionische Kust · Griekenland',
    whereSeaMeetsSky: 'Waar de Zee de Hemel Ontmoet',
    heroLongDesc: 'Twee luxe stenen appartementen boven de Ionische Zee — word wakker met Corfu, val in slaap met zonsondergangen',
    viewApartments: 'Bekijk Appartementen',
    ratingLabel: 'Beoordeling 9.2', exceptionalBooking: 'Uitzonderlijk op Booking.com',
    onBookingCom: '9.2 op Booking.com',
    hilltopViews: '🏔️ Panoramisch Uitzicht', twoMinBeach: '🌊 2 min naar Strand', privatePool: '🏊 Privé Zwembad',
    instantConfirmation: 'Directe Bevestiging', bookWithConfidence: 'Boek met vertrouwen',
    freeCancellation: 'Gratis Annulering', flexibleBooking: 'Flexibele boekingsopties',
    whereMedComesAlive: 'Waar de Middellandse Zee Tot Leven Komt',
    indexIntroDesc: 'Op een schilderachtige heuvel in het charmante dorpje Sivota bieden onze stenen appartementen panoramisch uitzicht op de Ionische Zee en Corfu.',
    heraPanoramicDesc: 'Een panoramisch toevluchtsoord met onbeperkt uitzicht op de Ionische Zee en Corfu.',
    zeusPoolDesc: 'Een luxe toevluchtsoord met indrukwekkend privézwembad en panoramisch uitzicht.',
    seaViewsTerrace: 'Zeezicht & terras', privatePoolCourtyard: 'Privézwembad & binnenplaats',
    twoBeds: '2 Bedden', seaViews: 'Zeezicht', wiFi: 'Wi-Fi', ac: 'Airco',
    kitchen: 'Keuken', terrace: 'Terras', pool: 'Zwembad', views: 'Uitzicht',
    twoBedrooms: '2 Slaapkamers', freeWiFi: 'Gratis Wi-Fi', fullKitchen: 'Volledig Uitgeruste Keuken',
    privatePoolLabel: 'Privézwembad', panoramicViews: 'Panoramisch Uitzicht',
    seaCorfuViews: 'Zee- & Corfu-zicht', privateTerrace: 'Privéterras',
    airConditioning: 'Airconditioning', highSpeedWiFi: 'Snel Wi-Fi',
    smartTV: 'Smart TV', washingMachine: 'Wasmachine',
    stoneVeranda: 'Stenen Veranda', panoramicSeaViews: 'Panoramisch Zeezicht',
    bestViewsQuote: '"Het mooiste uitzicht van heel Sivota"', ourGuests: '— Onze Gasten',
    medLivingFinest: 'Mediterraan Leven op z\'n Best',
    sunDrenchedTerraces: 'Zonovergoten Terrassen', sunDrenchedDesc: 'Ontbijt in de zon, dineren onder de sterren',
    endlessBlueViews: 'Eindeloos Blauw', endlessBlueDesc: 'Panorama op de Ionische Zee en Corfu',
    crystalClearBeaches: 'Kristalheldere Stranden', crystalClearDesc: 'Verborgen baaien en turquoise water',
    authenticGreekLife: 'Authentiek Grieks Leven', authenticGreekDesc: 'Taverna\'s, havens en eilandavonturen',
    sunSeaStone: 'Zon, Zee & Steen',
    islandHoppingFromSivota: 'Eilandhoppen vanuit Sivota',
    islandHoppingDesc: 'Ontdek de juwelen van de Ionische Zee — allemaal binnen handbereik',
    readAllReviews: 'Alle beoordelingen lezen',
    startPlanning: 'Begin met Plannen',
    yourMedEscape: 'Uw Mediterrane Ontsnapping Wacht',
    bookDirectlyBestRates: 'Boek direct voor de beste tarieven en een persoonlijk welkom van Dimitris',
    yourStoryStartsHere: 'Uw Verhaal Begint Hier',
    readyExperienceSivotaRoyal: 'Klaar voor Sivota Royal?',
    readyExperienceSivota: 'Klaar voor Sivota?',
    bookDirectlyBestRatesDimitris: 'Direct boeken voor de beste tarieven',
    builtWithLove: 'Gebouwd met Liefde, Gebaad in Zonlicht',
    notJustPlace: 'Dit is Niet Zomaar een Verblijfplaats',
    aboutIntro1: 'Stel u voor dat u wakker wordt met het geluid van de zee en op een zonnig terras stapt.',
    aboutIntro2: 'Sivota Royal is niet ontworpen op kantoor — het is met de hand gebouwd, steen voor steen.',
    whatSetsUsApart: 'Wat Ons Onderscheidt', moreThanAccommodation: 'Meer dan een Verblijf',
    handcrafted: 'Handgemaakt', handcraftedDesc: 'Elke steen met de hand geplaatst, elk detail overwogen.',
    bestViews: 'Beste Uitzicht', bestViewsDesc: 'Panoramische terrassen op de Ionische Zee en Corfu.',
    twoMinToBeach: '2 Min naar Strand', twoMinToBeachDesc: 'Kristalhelder water op een steenworp afstand.',
    authentic: 'Authentiek', authenticDesc: 'Geen drukte. Het ritme van het Griekse kustleven.',
    theSetting: 'De Omgeving', everyMorningGift: 'Elke Ochtend Voelt als een Geschenk',
    settingDesc1: 'Gouden zonlicht stroomt door uw ramen. U stapt op uw privéterras.',
    settingDesc2: 'Dit is geen droom — dit is uw ochtend bij Sivota Royal.',
    greeceBestKeptSecret: 'Het Bestbewaarde Geheim van Griekenland',
    locationDesc1: 'Sivota is een verborgen juweel aan de kust van Thesprotia.',
    locationDesc2: 'Ontdek verborgen baaien, dineer in de haven, bewonder de zonsondergang.',
    quoteNotHotel: '"We hebben geen hotel gebouwd. We hebben de plek gebouwd waar we zelf willen verblijven."',
    ownerName: '— Dimitris Vlachos, Eigenaar',
    theProperty: 'Het Eigendom', everyDetailConsidered: 'Elk Detail, Doordacht',
    personalWelcome: 'Een Persoonlijk Welkom', meetDimitris: 'Ontmoet Dimitris',
    dimitrisQuote1: '"Ik ben Dimitris, en Sivota Royal is mijn levenswerk."',
    dimitrisQuote2: '"Wanneer u aankomt, zal ik u persoonlijk verwelkomen."',
    yourHost: 'Uw Gastheer', dimitrisMadeFeel: '"Dimitris gaf ons het gevoel dat we familie waren"',
    twoExceptionalResidences: 'Twee uitzonderlijke residenties, elk met hun eigen karakter.',
    heraExtendedDesc: 'Dit prachtige panoramische appartement biedt onbeperkt zeezicht.',
    zeusExtendedDesc: 'Een luxe appartement met indrukwekkend privézwembad.',
    stayDetails: 'Verblijfsgegevens', yourDetails: 'Uw Gegevens',
    selectYourDates: 'Selecteer uw data', total: 'Totaal', discount: 'Korting',
    couponCode: 'Couponcode', enterCode: 'Voer code in', apply: 'Toepassen',
    continueBtn: 'Doorgaan', back: 'Terug', sending: 'Verzenden...',
    yourSelection: 'Uw Selectie', editSelection: 'Selectie bewerken',
    apartment: 'Appartement',
    chatWithUs: 'Chat Direct met Ons', chatWithUsDesc: 'Liever berichten? Bereik ons op WhatsApp.',
    chatOnWhatsApp: '💬 Chat op WhatsApp',
    noPricingSet: 'Geen prijs ingesteld — we bevestigen het tarief.',
    minimumStay: 'Minimaal verblijf {min} nachten',
    thankYouEnquiry: 'Bedankt! Uw aanvraag is verzonden.',
    fillNameEmail: 'Vul naam en e-mail in.', selectApartment: 'Selecteer een appartement.',
    selectDates: 'Selecteer data.', invalidCoupon: 'Ongeldige couponcode',
    viewOnBooking: 'Bekijk op Booking.com',
    fillRequiredFields: 'Vul alle verplichte velden in.',
    messageSent: 'Bericht verzonden! We nemen snel contact op.',
    joinOurGuests: 'Word Onze Gast', createYourStory: 'Schrijf Uw Eigen Verhaal',
    featuredQuote: '"Het mooiste uitzicht van heel Sivota — een onvergetelijke ervaring"',
    featuredQuoteAuthor: '— Maria K., Athene',
    hiddenCovesDesc: 'Verborgen baaien, eilandavonturen en authentieke Griekse ervaringen',
    secludedBeaches: 'Afgelegen Stranden', secludedBeachesDesc: 'Kristalhelder water en witte kiezelstranden.',
    sivotaCoastline: 'Kust van Sivota', boatTrips: 'Boottochten',
    boatTripsDesc: 'Ontdek de Ionische eilanden en verborgen zeegrotten.', ionianSea: 'Ionische Zee',
    sivotaHarbour: 'Haven van Sivota', sivotaHarbourDesc: 'Wandel langs de haven en geniet van verse vis.',
    sivotaVillage: 'Dorp Sivota', localDining: 'Lokale Keuken',
    localDiningDesc: 'Van taverna\'s aan zee tot bergrestaurants.', sivotaSurrounds: 'Sivota & Omgeving',
    islandHopping: 'Eilandhoppen', jewelsOfIonian: 'Juwelen van de Ionische Zee',
    islandHoppingIntro: 'Vanuit Sivota, ontdek drie van de mooiste eilanden van de Middellandse Zee',
    corfuDesc: 'UNESCO oude stad, Achilleion paleis en prachtige stranden.',
    corfuTime: '1,5 uur per veerboot', paxosDesc: 'Zeegrotten, turquoise water en charmante dorpjes.',
    paxosTime: '1 uur per boot', antipaxosDesc: 'Voutoumi en Vrika — twee van de mooiste stranden van Griekenland.',
    antipaxosTime: '1,5 uur per boot',
    thesprotiaRegion: 'Regio Thesprotia', natureHiking: 'Natuur & Wandelen',
    natureHikingDesc1: 'Ontdek olijfgaarden, oude paden en groene heuvels.',
    natureHikingDesc2: 'Van bergdorpjes tot kustpaden.',
    yourGateway: 'Uw Toegangspoort', startIonianAdventure: 'Begin Uw Ionisch Avontuur',
    ionianAdventureDesc: 'Vanuit Sivota, ontdek Corfu, Paxos en Antipaxos.',
    heraHeroDesc: 'Het mooiste uitzicht van Sivota — eindeloos Ionisch blauw en Corfu-zonsondergangen',
    masterBedroom: 'Hoofdslaapkamer',
    heraMasterTitle: 'Droomkamer met Ongeëvenaarde Zonsondergangen',
    heraMasterDesc: 'Deze prachtige panoramische slaapkamer biedt onbeperkt uitzicht op de Ionische Zee.',
    secondaryBedroom: 'Tweede Slaapkamer',
    heraSecondaryTitle: 'Charmante Slaapkamer met Zeezicht',
    heraSecondaryDesc: 'Deze heerlijke tweede slaapkamer biedt comfortabel plaats aan drie gasten.',
    kitchenDining: 'Keuken & Eethoek',
    heraKitchenTitle: 'Luxe Keuken met Traditioneel Grieks Ontwerp',
    heraKitchenDesc: 'Deze luxe keuken met prachtig stenen en houten ontwerp.',
    livingRoom: 'Woonkamer',
    heraLivingTitle: 'Koninklijke Woonkamer met Traditionele Elegantie',
    heraLivingDesc: 'Deze grandioze woonkamer met traditioneel gesneden meubels.',
    secureYourDates: 'Reserveer uw data in ons toevluchtsoord op de heuvel.',
    exceptionalOnBooking: 'Uitzonderlijk · Booking.com',
    heraOverviewDesc1: 'Het Hera Appartement belichaamt gratie, warmte en tijdloze schoonheid.',
    heraOverviewDesc2: 'Word wakker met het geluid van golven, geniet van koffie op het terras.',
    zeusHeroDesc: 'Privézwembad, panoramisch Ionisch uitzicht en traditioneel steenwerk',
    zeusOverviewDesc1: 'Het Zeus Appartement trekt de aandacht met ruime interieurs en natuursteen.',
    zeusOverviewDesc2: 'Ontdek de betoverende binnenplaats met zwembad en elegante ligstoelen.',
    courtyardPool: 'Binnenplaats & Zwembad',
    zeusCourtTitle: 'Idyllische Binnenplaats met Zwembad en Panoramisch Uitzicht',
    zeusCourtDesc: 'Ontdek de betoverende binnenplaats — een groene oase met zwembad en bougainville.',
    zeusMasterTitle: 'Charmante Hoofdslaapkamer met Traditionele Houten Meubels',
    zeusMasterDesc: 'Stap de betoverende slaapkamer in met handgemaakt houten meubilair.',
    twinBedroom: 'Tweepersoonskamer',
    zeusTwinTitle: 'Gezellige Tweepersoonskamer met Balkon & Zeezicht',
    zeusTwinDesc: 'Met twee eenpersoonsbedden zorgt deze kamer voor een rustgevend verblijf.',
    stoneVerandaLabel: 'Stenen Veranda',
    zeusVerandaTitle: 'Traditionele Stenen Veranda met Ionisch Panorama',
    zeusVerandaDesc: 'De prachtige handgehouwen stenen veranda met houten dak.',
    secureYourDatesZeus: 'Reserveer uw data voor zwembad en panoramisch uitzicht.',
    seaViewBedrooms: 'Slaapkamers met zeezicht', premiumBedLinens: 'Premium beddengoed',
    fullyEquippedKitchen: 'Volledig uitgeruste keuken', privateTerraceAmenity: 'Privéterras',
    airConditioningAmenity: 'Airconditioning', highSpeedWiFiAmenity: 'Snel Wi-Fi',
    smartTVAmenity: 'Smart TV', washingMachineAmenity: 'Wasmachine',
    hairDryer: 'Haardroger', ironIroningBoard: 'Strijkijzer & strijkplank',
    toiletries: 'Toiletartikelen', beachTowels: 'Strandhanddoeken',
    stoneBuiltDesign: 'Stenen ontwerp', woodenDetails: 'Houten details',
    traditionalArchitecture: 'Traditionele architectuur', modernComforts: 'Modern comfort',
    privatePoolAmenity: 'Privézwembad', panoramicSeaViewsAmenity: 'Panoramisch zeezicht',
    spaciousCourtyard: 'Ruime binnenplaats',
    stoneBuiltVeranda: 'Stenen veranda', woodenFurnishings: 'Houten meubels',
    bougainvilleaPergola: 'Bougainville pergola', sunLoungers: 'Ligstoelen',
    weValuePrivacy: 'Wij Respecteren Uw Privacy',
    cookieDesc: 'Wij gebruiken alleen essentiële cookies voor de werking van de website. Geen tracking-cookies.',
    decline: 'Weigeren', accept: 'Accepteren',
  },
};

const languageNames: Record<Language, string> = {
  en: 'English', el: 'Ελληνικά', it: 'Italiano', de: 'Deutsch', fr: 'Français', nl: 'Nederlands',
};

const languageFlags: Record<Language, string> = {
  en: '🇬🇧', el: '🇬🇷', it: '🇮🇹', de: '🇩🇪', fr: '🇫🇷', nl: '🇳🇱',
};

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  languages: Language[];
  languageNames: Record<Language, string>;
  languageFlags: Record<Language, string>;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<Language>('en');

  const t = (key: string) => translations[language]?.[key] || translations.en[key] || key;

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, languages: ['en', 'el', 'it', 'de', 'fr', 'nl'], languageNames, languageFlags }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
}
